/**
 * The captain's HOUSE interior (Onda 8) — a warm galleon-stern cabin the owner decorates. It REUSES the
 * whole walkable-port machinery by extending {@link PortScene}: the avatar system, emote/prediction sync,
 * third-person camera and per-frame render are inherited verbatim. HouseScene only swaps the static-world
 * build (a 22×14 timber cabin instead of a plaza) and adds the furniture + trophy-wall layers driven by
 * S_HOUSE_STATE, plus a decorate ghost/preview + an isometric edit camera.
 *
 * Style matches the port: chunky Sea-of-Thieves timber & iron, never realistic. Furniture is PROCEDURAL
 * (vertex-coloured merged geometry, ONE InstancedMesh per kind) except the hero Captain's Berth, which loads
 * a real GLB. House-local (x,y) → three.js world (x, 0, y), same convention as the port.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  HOUSE, HOUSE_SHELL, FurnitureKind, furnitureById, furnitureFootprint, TrophySlotKind, inHouseExit,
  dayNightPhase, clampHouseScale, furnitureYaw, furnitureOffset, type HouseStateMsg, type PortObstacle,
} from '@piratas/protocol';
import { nearestSeat, buildOceanGLSL, oceanEnvUniforms, type SeatSpot } from '@piratas/sim';
import { PortScene } from './portscene.js';
import { cityPaletteAt } from './citytime.js';
import { getSetting, onSettingChange, type GraphicsPreset } from './settings.js';
import type { Renderer } from './render.js';

const UP = new THREE.Vector3(0, 1, 0);
/** Cool moonlight the cabin's fill light is nudged toward at deep night (harmonizes with the exterior city
 *  cycle without duplicating it — the hearth/candles keep the room warm; this only cools the upper ambient). */
const HOUSE_NIGHT_TINT = new THREE.Color(0x2a4472);
const WALL_H = 3.3;            // interior wall height (m)
/** Leva 3 (Casa do Capitão — móveis do dono): the owner's Meshy skull berth REPLACES the old procedural/
 *  captain_bed hero for the BED kind — a skull-carved frame draped in the Jolly Roger. Baked like the others. */
const BED_URL = '/models/skull_bed/skull_bed.opt.glb';
/** Onda 8 "Interior Vivo" hero GLBs (already on disk, commit dd7bfdd) — the iron stove becomes both the
 *  fixed hearth of the shell AND the render of the Smith IRON_STOVE furniture; the candelabrum becomes the
 *  Smith CANDELABRA. Loaded once, baked exactly like the captain berth. */
const STOVE_URL = '/models/iron_stove/iron_stove.opt.glb';
const CANDELABRUM_URL = '/models/candelabrum/candelabrum.opt.glb';
/** Leva 2 (Onda de Arte) furniture heroes — a felt Card Table (new Carpenter kind) plus GLB models that
 *  replace the procedural GLOBE (Smith) and AQUARIUM (Alchemist). Baked exactly like the stove/candelabrum. */
const CARD_TABLE_URL = '/models/captain_card_table/captain_card_table.opt.glb';
const GLOBE_URL = '/models/iron_globe/iron_globe.opt.glb';
const AQUARIUM_URL = '/models/cabin_aquarium/cabin_aquarium.opt.glb';
/** Leva 3 (Casa do Capitão — móveis do dono) heroes: owner-generated Meshy GLBs that replace the procedural
 *  Chart Table / Bookshelf / Strange Lamp / Rum Barrel, plus the brand-new Brass Spyglass (Smith) kind. */
const CHART_TABLE_URL = '/models/chart_table/chart_table.opt.glb';
const BOOKSHELF_URL = '/models/bookshelf/bookshelf.opt.glb';
const STRANGE_LAMP_URL = '/models/strange_lamp/strange_lamp.opt.glb';
const RUM_BARREL_URL = '/models/rum_barrel/rum_barrel.opt.glb';
const SPYGLASS_URL = '/models/spyglass/spyglass.opt.glb';
/** The trophy wall's "Flagship" slot shows a MINI of the captain's current ship — the real hull GLB (per
 *  ShipClassId: sloop/brigantine/galleon/frigate), lazily loaded & baked to ~0.5 m and stood on the map table
 *  (before: a crude vertex-coloured blob that read as a cardboard box). Self-contained here — housescene owns
 *  its own GLB pipeline; the sea renderer (render.ts) is a separate agent's territory. */
const SHIP_MODEL_URLS: Record<number, string> = {
  0: '/models/sloop/sloop.opt.glb',        // ShipClassId.SLOOP
  1: '/models/brigantine/brigantine.opt.glb', // BRIGANTINE
  2: '/models/galleon/galleon.opt.glb',    // GALLEON
  3: '/models/frigate/frigate.opt.glb',    // FRIGATE
  // Onda Frota (v47): the trophy "mini flagship" in the cabin shows the captain's actual new hull.
  4: '/models/cutter/cutter.opt.glb',               // CUTTER
  5: '/models/fluyt/fluyt.opt.glb',                 // FLUYT
  6: '/models/east_indiaman/east_indiaman.opt.glb', // EAST_INDIAMAN
};
/**
 * ExteriorVista config — the ONE place to tune the living sea outside the stern gallery (no magic numbers in
 * the render code). The old flat window_sea_{day,dusk,night}.webp wallpaper is GONE; the stern windows now
 * look onto a single continuous, animated 3D diorama (near Gerstner ocean + far sky/horizon backdrop + distant
 * islands/ship/gulls) rendered offscreen once per frame and sampled by the glass as a true see-through PORTAL.
 * The solid stern wall is the natural mask, so nothing can ever leak past it — trophy wall, decorate raycasts
 * and every camera stay untouched. See {@link buildVista}. Colours are per-time-of-day (see VISTA_KEYS).
 */
const VISTA = {
  // (1) NEAR OCEAN — real Gerstner geometry (reuses the game's shared OCEAN_WAVES), real parallax + foam.
  oceanWaveAmp: 1.35,       // swell height multiplier over the shared sea table (exaggerated so waves read through a small window)
  oceanWaveSpeed: 0.6,      // slow time scale of the swell
  oceanScale: 1.15,         // wavelength stretch (wider, "painted" crests read better through a small window)
  seaLevel: -1.4,           // world-Y of the calm surface (below the cabin floor → sea sits under the sill)
  oceanSpanX: 190, oceanSpanZ: 170, // metres; world-FIXED + large so every in-room camera stays over water
  oceanCenterZ: 60,         // north of the stern wall (z≈14) so the sheet reaches from the sill out to the haze
  oceanSubdiv: { low: 28, medium: 44, high: 68 }, // grid resolution per graphics tier (1 draw either way)
  horizonHaze: 0.40,        // fraction of the sheet (near→far) over which the sea dissolves into the horizon band
  // (2) FAR SKY/HORIZON BACKDROP — a big curved gradient wall (slow parallax); paints sky + horizon into the
  //     down-looking window view and hides the sea's far edge in haze.
  backdropRadius: 130, backdropTop: 90, backdropBottom: -60, backdropHorizonY: -9, // world-Y the horizon paints at
  cloudSpeed: 0.004, cloudScale: 3.2,
  // (3) DISTANT LIFE — silhouettes on the horizon, a lone crossing ship, a few gulls. Tiered.
  distantIslandDensity: { low: 0, medium: 3, high: 5 },
  distantShipEnabled: true, distantShipSpeed: 0.4,
  gullCount: { low: 0, medium: 2, high: 4 }, // "rare" gulls — not a flock (each is one cheap offscreen billboard)
  // (4) WINDOW GLASS — subtle Fresnel tint + a faint interior reflection; never a mirror, never hides the sea.
  glassTint: 0x14323e, glassOpacity: 0.12, glassReflection: 0.16,
  // Offscreen portal target resolution, as a fraction of the screen, per tier (0 = disabled → cheap gradient).
  rtScale: { low: 0, medium: 0.5, high: 0.62 },
} as const;

/** One time-of-day key for the vista (sky + sea + sun). Colours are THREE.Color; sunY is 0 (horizon)…1 (high). */
interface VistaKey { p: number; skyTop: THREE.Color; skyHaze: THREE.Color; seaDeep: THREE.Color; seaShallow: THREE.Color; sun: THREE.Color; sunI: number; sunY: number; star: number; }
const vk = (p: number, skyTop: number, skyHaze: number, seaDeep: number, seaShallow: number, sun: number, sunI: number, sunY: number, star: number): VistaKey =>
  ({ p, skyTop: new THREE.Color(skyTop), skyHaze: new THREE.Color(skyHaze), seaDeep: new THREE.Color(seaDeep), seaShallow: new THREE.Color(seaShallow), sun: new THREE.Color(sun), sunI, sunY, star });
/** Keyframes at the sea's phase anchors (0=midnight · 0.5=noon · 0.84 sunset). Teal-and-gold Sea-of-Robin
 *  palette family — deep blue-green water, blue-petrol sky, warm horizon; midnight is moonlit, never black. */
const VISTA_KEYS: readonly VistaKey[] = [
  //  p     skyTop    skyHaze   seaDeep   seaShall  sun       sunI  sunY  star
  vk(0.00, 0x0e1b3a, 0x27324f, 0x081f2c, 0x123646, 0xa9c0f0, 0.30, 0.62, 1.00), // midnight — moonlit blue
  vk(0.12, 0x3a4f86, 0xd59a86, 0x123a48, 0x2b6d78, 0xffb579, 0.85, 0.10, 0.30), // dawn — pink/gold on the sea line
  vk(0.30, 0x4f86bd, 0xbfe0ea, 0x0d3f4f, 0x2f8896, 0xfff0d0, 1.15, 0.42, 0.02), // morning
  vk(0.50, 0x3f7fc4, 0xcfeaf2, 0x0c485c, 0x2f97a6, 0xfff4d8, 1.35, 0.72, 0.00), // noon — bright teal sea
  vk(0.72, 0x5f79b0, 0xf0cfa0, 0x114452, 0x2f8f96, 0xffd9a0, 1.10, 0.22, 0.06), // golden hour
  vk(0.84, 0x3a3f7c, 0xd88a6a, 0x122f42, 0x2a6a74, 0xff7a4a, 0.80, 0.06, 0.42), // sunset — gold → purple
  vk(0.93, 0x1c274f, 0x3a3f66, 0x0a2434, 0x184048, 0x9a8ac8, 0.42, 0.5, 0.88),  // twilight — deep blue
  vk(1.00, 0x0e1b3a, 0x27324f, 0x081f2c, 0x123646, 0xa9c0f0, 0.30, 0.62, 1.00), // → midnight (wrap)
];

/** Shared clock for the cloth-sway vertex shader (one value drives every curtain + the shell banner). Module
 *  level because the shell is built inside super() before instance fields exist. Advanced in updateInterior. */
const SWAY_TIME = { value: 0 };

/* palette */
const C = {
  wood: 0x6e4a2a, woodDark: 0x4a3320, timber: 0x38271a, plank: 0x5a3d24,
  iron: 0x35353c, ironLt: 0x54545e, gold: 0xc8901e,
  cloth: 0xb03a3a, clothDark: 0x7a1f1f, mattress: 0xe6dcc0, pillow: 0xf2ecdc,
  glass: 0x3f7f96, leaf: 0x3a7a34, parch: 0xd8c49a, sea: 0x2c6d84,
} as const;

/** One baked furniture template: solid geometry + optional emissive-accent geometry + point-light spec. */
interface FurnBake {
  geo: THREE.BufferGeometry;
  mat: THREE.Material;
  glowGeo?: THREE.BufferGeometry;
  glowMat?: THREE.Material;
  light?: { color: number; intensity: number; dist: number; y: number };
  isGlb?: boolean;
  scale?: number;   // GLB fit-to-footprint scale
  groundY?: number; // GLB base offset
}

type Part = { geo: THREE.BufferGeometry; color: number };

/* ---- tiny geometry helpers (template-local space, y up from the floor) ---- */
function box(w: number, h: number, d: number, x: number, y: number, z: number, color: number, rotY = 0): Part {
  const g = new THREE.BoxGeometry(w, h, d); if (rotY) g.rotateY(rotY); g.translate(x, y, z); return { geo: g, color };
}
function cyl(rt: number, rb: number, h: number, seg: number, x: number, y: number, z: number, color: number): Part {
  const g = new THREE.CylinderGeometry(rt, rb, h, seg); g.translate(x, y, z); return { geo: g, color };
}
function sph(r: number, x: number, y: number, z: number, color: number): Part {
  const g = new THREE.SphereGeometry(r, 12, 10); g.translate(x, y, z); return { geo: g, color };
}
function legs(w: number, d: number, h: number, color: number): Part[] {
  const out: Part[] = [];
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) out.push(cyl(0.07, 0.08, h, 6, sx * (w / 2 - 0.12), h / 2, sz * (d / 2 - 0.12), color));
  return out;
}
/** A dusty dust-sheet draped over an un-earned trophy mount — a low, softly-lumpy dome so an EMPTY slot reads as
 *  "a covered exhibit, waiting to be unveiled" instead of a bare cardboard box. Merged into the slot's one mesh. */
function dustSheet(x: number, y: number, z: number, rx: number, ry: number, rz: number): Part {
  const g = new THREE.SphereGeometry(1, 14, 10);
  // pinch a couple of gentle folds into the drape so it reads as cloth, not an egg
  const pos = g.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) { const px = pos.getX(i), py = pos.getY(i), pz = pos.getZ(i); const f = 1 + 0.06 * Math.sin(px * 7) * Math.sin(pz * 7); pos.setXYZ(i, px * f, py, pz * f); }
  g.scale(rx, ry, rz); g.translate(x, y, z);
  return { geo: g, color: 0xb2a892 }; // dusty linen
}

/** Procedural parts for a furniture kind, sized to its cell footprint (metres, at rotation 0). Returns the
 *  solid body (vertex-coloured), an optional glow accent (single emissive colour) and a point-light hint. */
function furnitureParts(kind: number): { solid: Part[]; glow?: Part[]; glowColor?: number; light?: FurnBake['light'] } {
  const f = furnitureById(kind)?.foot ?? { w: 1, d: 1 };
  const w = f.w, d = f.d;
  switch (kind) {
    case FurnitureKind.TABLE:
      return { solid: [box(w - 0.1, 0.16, d - 0.1, 0, 0.92, 0, C.wood), ...legs(w, d, 0.9, C.woodDark)] };
    case FurnitureKind.CHAIR:
      return { solid: [box(0.56, 0.12, 0.56, 0, 0.5, 0, C.wood), box(0.56, 0.62, 0.1, 0, 0.85, -0.22, C.wood), ...legs(0.7, 0.7, 0.5, C.woodDark)] };
    case FurnitureKind.BED:
      return { solid: [
        box(w - 0.1, 0.4, d - 0.1, 0, 0.32, 0, C.woodDark),
        box(w - 0.4, 0.28, d - 0.4, 0, 0.62, 0, C.mattress),
        box(0.7, 0.22, d - 0.6, -(w / 2 - 0.55), 0.82, 0, C.pillow),
        cyl(0.09, 0.09, 1.5, 6, -(w / 2 - 0.12), 0.75, -(d / 2 - 0.12), C.woodDark),
        cyl(0.09, 0.09, 1.5, 6, -(w / 2 - 0.12), 0.75, (d / 2 - 0.12), C.woodDark),
        cyl(0.09, 0.09, 1.1, 6, (w / 2 - 0.12), 0.55, -(d / 2 - 0.12), C.woodDark),
        cyl(0.09, 0.09, 1.1, 6, (w / 2 - 0.12), 0.55, (d / 2 - 0.12), C.woodDark),
      ] };
    case FurnitureKind.BOOKSHELF: {
      const solid: Part[] = [box(w - 0.1, 2.1, 0.34, 0, 1.05, 0, C.woodDark)];
      for (const y of [0.5, 1.05, 1.6]) solid.push(box(w - 0.2, 0.06, 0.28, 0, y, 0.02, C.wood));
      const cols = [0x8a2f2f, 0x2f5a8a, 0x2f7a4a, 0xb08a2f];
      for (let i = 0; i < 6; i++) solid.push(box(0.12, 0.34, 0.22, -(w / 2) + 0.25 + i * ((w - 0.4) / 6), 0.72 + (i % 3) * 0.55, 0.03, cols[i % cols.length]!));
      return { solid };
    }
    case FurnitureKind.BENCH:
      return { solid: [box(w - 0.1, 0.66, d - 0.1, 0, 0.33, 0, C.wood), box(w - 0.05, 0.14, d - 0.05, 0, 0.72, 0, C.woodDark), box(0.1, 0.7, d, -(w / 2 - 0.3), 0.35, 0, C.iron), box(0.1, 0.7, d, (w / 2 - 0.3), 0.35, 0, C.iron)] };
    case FurnitureKind.CHART_TABLE:
      return { solid: [box(w - 0.1, 0.14, d - 0.1, 0, 0.95, 0, C.wood), box(w - 0.6, 0.03, d - 0.6, 0, 1.03, 0, C.parch), ...legs(w, d, 0.92, C.woodDark)] };
    case FurnitureKind.CARD_TABLE: // round felt table on ONE chunky pedestal (procedural fallback for the GLB hero)
      return { solid: [
        cyl(w * 0.46, w * 0.46, 0.12, 16, 0, 0.92, 0, C.wood),   // round wood rim
        cyl(w * 0.4, w * 0.4, 0.04, 16, 0, 0.99, 0, 0x2f6d43),   // green felt top
        cyl(0.26, 0.34, 0.86, 12, 0, 0.45, 0, C.woodDark),       // stout central pedestal (no thin legs)
        cyl(0.42, 0.46, 0.1, 12, 0, 0.05, 0, C.woodDark),        // wide grounded foot
      ] };
    case FurnitureKind.WALL_LAMP:
      return { solid: [box(0.1, 0.1, 0.4, 0, 1.5, -0.28, C.iron), box(0.3, 0.44, 0.3, 0, 1.4, -0.08, C.iron)],
        glow: [box(0.18, 0.3, 0.18, 0, 1.4, -0.08, 0)], glowColor: 0xffb347, light: { color: 0xff9d3a, intensity: 0.9, dist: 6, y: 1.4 } };
    case FurnitureKind.CANDELABRA:
      return { solid: [cyl(0.24, 0.3, 0.12, 10, 0, 0.06, 0, C.iron), cyl(0.05, 0.05, 1.1, 6, 0, 0.6, 0, C.iron), box(0.7, 0.05, 0.05, 0, 1.1, 0, C.iron)],
        glow: [sph(0.07, -0.3, 1.2, 0, 0), sph(0.07, 0, 1.24, 0, 0), sph(0.07, 0.3, 1.2, 0, 0)], glowColor: 0xffd27a, light: { color: 0xffcf7a, intensity: 0.8, dist: 6, y: 1.2 } };
    case FurnitureKind.IRON_STOVE:
      return { solid: [box(w - 0.3, 1.4, d - 0.3, 0, 0.7, 0, C.iron), cyl(0.16, 0.18, 1.4, 8, 0.5, 2.1, -(d / 2 - 0.5), C.ironLt), box(w - 0.2, 0.14, d - 0.2, 0, 1.45, 0, C.ironLt)],
        glow: [box(0.7, 0.6, 0.08, 0, 0.55, (d / 2 - 0.16), 0)], glowColor: 0xff7a1a, light: { color: 0xff8c2a, intensity: 1.4, dist: 9, y: 0.7 } };
    case FurnitureKind.GLOBE:
      return { solid: [cyl(0.26, 0.3, 0.1, 10, 0, 0.05, 0, C.woodDark), cyl(0.05, 0.05, 0.7, 6, 0, 0.45, 0, C.wood), sph(0.34, 0, 1.0, 0, 0x3a6ea5), (() => { const g = new THREE.TorusGeometry(0.38, 0.03, 6, 20); g.rotateX(Math.PI / 2); g.translate(0, 1.0, 0); return { geo: g, color: C.gold }; })()] };
    case FurnitureKind.AQUARIUM:
      return { solid: [box(w - 0.2, 0.24, d - 0.1, 0, 0.5, 0, C.woodDark), box(w - 0.3, 0.8, d - 0.25, 0, 1.05, 0, C.glass)], light: { color: 0x4fd0ff, intensity: 0.5, dist: 5, y: 1.0 } };
    case FurnitureKind.POTION_SHELF: {
      const solid: Part[] = [box(w - 0.1, 1.4, 0.3, 0, 1.0, 0, C.woodDark)];
      for (const y of [0.55, 1.05, 1.5]) solid.push(box(w - 0.2, 0.05, 0.24, 0, y, 0.02, C.wood));
      const glow: Part[] = []; const cols = [0x66ff9a, 0x9a5cff, 0xff6699];
      for (let i = 0; i < 5; i++) glow.push(cyl(0.06, 0.08, 0.22, 6, -(w / 2) + 0.3 + i * ((w - 0.5) / 5), 0.72 + (i % 3) * 0.5, 0.02, cols[i % 3]!));
      return { solid, glow, glowColor: 0x88ffcc, light: { color: 0x77ffbb, intensity: 0.5, dist: 5, y: 1.0 } };
    }
    case FurnitureKind.STRANGE_LAMP:
      return { solid: [cyl(0.2, 0.24, 0.14, 8, 0, 0.07, 0, C.iron), cyl(0.05, 0.05, 0.8, 6, 0, 0.5, 0, C.iron)],
        glow: [sph(0.22, 0, 1.05, 0, 0)], glowColor: 0x9a5cff, light: { color: 0x9a5cff, intensity: 1.0, dist: 7, y: 1.05 } };
    case FurnitureKind.FISH_TROPHY:
      return { solid: [box(0.8, 0.5, 0.1, 0, 1.4, -0.04, C.wood), (() => { const g = new THREE.SphereGeometry(0.18, 10, 8); g.scale(1.8, 1, 0.5); g.translate(0, 1.4, 0.06); return { geo: g, color: 0x7fa8bd }; })()] };
    case FurnitureKind.RUG:
      return { solid: [box(w, 0.04, d, 0, 0.03, 0, C.cloth), box(w - 0.3, 0.05, d - 0.3, 0, 0.045, 0, C.clothDark)] };
    case FurnitureKind.CURTAIN:
      return { solid: [box(w, 0.08, 0.08, 0, WALL_H - 0.4, -0.35, C.iron), box(w - 0.1, 1.8, 0.1, 0, WALL_H - 1.4, -0.28, C.clothDark)] };
    case FurnitureKind.DECO_BARREL:
      return { solid: [cyl(0.34, 0.3, 0.9, 12, 0, 0.45, 0, C.wood), (() => { const g = new THREE.TorusGeometry(0.36, 0.03, 6, 16); g.rotateX(Math.PI / 2); g.translate(0, 0.7, 0); return { geo: g, color: C.iron }; })(), (() => { const g = new THREE.TorusGeometry(0.36, 0.03, 6, 16); g.rotateX(Math.PI / 2); g.translate(0, 0.2, 0); return { geo: g, color: C.iron }; })()] };
    case FurnitureKind.CRATE:
      return { solid: [box(0.85, 0.85, 0.85, 0, 0.45, 0, C.wood), box(0.9, 0.1, 0.9, 0, 0.86, 0, C.woodDark), box(0.9, 0.1, 0.9, 0, 0.05, 0, C.woodDark)] };
    case FurnitureKind.PAINTING:
      return { solid: [box(0.86, 0.66, 0.08, 0, 1.6, -0.04, C.gold), box(0.66, 0.46, 0.04, 0, 1.6, 0.02, 0x3a6a8a)] };
    case FurnitureKind.PLANT:
      return { solid: [cyl(0.28, 0.22, 0.42, 10, 0, 0.21, 0, 0x9a5a3a), sph(0.34, 0, 0.7, 0, C.leaf), sph(0.24, 0.18, 0.9, 0.05, C.leaf), sph(0.22, -0.15, 0.86, -0.06, C.leaf)] };
    default:
      return { solid: [box(w - 0.1, 0.8, d - 0.1, 0, 0.4, 0, C.wood)] };
  }
}

/** Give a geometry a flat vertex-colour attribute (so many-coloured parts merge into ONE material). */
function paint(geo: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const c = new THREE.Color(hex);
  const n = geo.attributes.position!.count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b; }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return geo;
}
/** Merge parts into one buffer geometry, keeping position/normal/color consistent for a clean merge. */
function mergeParts(parts: Part[], colored: boolean): THREE.BufferGeometry | null {
  const geos = parts.map((p) => {
    const g = p.geo;
    if (!g.attributes.normal) g.computeVertexNormals();
    for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'normal') g.deleteAttribute(name);
    if (colored) paint(g, p.color);
    return g;
  });
  if (!geos.length) return null;
  return geos.length === 1 ? geos[0]! : (mergeGeometries(geos, false) ?? geos[0]!);
}

export class HouseScene extends PortScene {
  private furnitureRoot!: THREE.Group;
  private slotRoot!: THREE.Group;
  private furnBakes = new Map<number, FurnBake>();
  private ghost: THREE.Mesh | null = null;
  private ghostKind = -1;
  private raycaster = new THREE.Raycaster();
  private floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private editCam = false;
  private state: HouseStateMsg | null = null;
  private bedBake: FurnBake | null = null;
  private bedLoading = false;
  /** Roof-cull caches (lazily collected from staticRoot on first frame — buildStaticWorld runs during super()
   *  and can only tag meshes, not populate HouseScene fields). roof=1 → ceiling/beams, roof=2 → mezzanine. */
  private _roofNodes: THREE.Object3D[] | null = null;
  private _mezzNodes: THREE.Object3D[] | null = null;

  /* ------- "Interior Vivo" (render-only polish) ------- */
  /** Hero interior GLBs, baked like the berth (geo+mat+fit). Null until preloadInterior() resolves. */
  private stoveBake: FurnBake | null = null;
  private candelabrumBake: FurnBake | null = null;
  private cardTableBake: FurnBake | null = null;   // Leva 2: Card Table hero GLB
  private globeBake: FurnBake | null = null;        // Leva 2: iron Globe hero GLB (was procedural)
  private aquariumBake: FurnBake | null = null;     // Leva 2: cabin Aquarium hero GLB (was procedural)
  private chartTableBake: FurnBake | null = null;   // Leva 3: treasure-map Chart Table hero GLB (was procedural)
  private bookshelfBake: FurnBake | null = null;    // Leva 3: necromancer-library Bookshelf hero GLB (was procedural)
  private strangeLampBake: FurnBake | null = null;  // Leva 3: jelly-in-a-bottle Strange Lamp hero GLB (was procedural)
  private rumBarrelBake: FurnBake | null = null;    // Leva 3: rum-barrel workshop hero GLB (was procedural DECO_BARREL)
  private spyglassBake: FurnBake | null = null;     // Leva 3: brass Spyglass hero GLB (new Smith kind)
  /** Trophy "Flagship" slot: baked mini ship GLBs keyed by ShipClassId (loaded on demand for the owner's class). */
  private shipBakes = new Map<number, FurnBake>();
  private shipLoading = new Set<number>();
  private interiorLoading = false;
  private interiorReady = false;
  /** A dedicated group for the fixed hearth stove GLB (swapped in over the procedural mantel). */
  private hearthRoot = new THREE.Group();
  /** Living-light + ambience layers, all collected/created lazily (shell is built during super()). */
  private _hearthLight: THREE.PointLight | null = null;   // the shell hearth light, driven with an organic flicker
  private _hearthProc: THREE.Object3D[] | null = null;    // procedural mantel/embers — hidden once the GLB lands
  private _emberMat: THREE.MeshStandardMaterial | null = null; // procedural ember glow (fallback hearth)
  private _glbEmberMat: THREE.MeshBasicMaterial | null = null;  // additive ember at the GLB stove mouth
  private _winPanes: THREE.Mesh[] | null = null;          // stern window panes → time-of-day seascape blend
  private _houseHemi: THREE.HemisphereLight | null = null; // shell fill light — cool-tinted at night to harmonize with the city cycle
  private _houseHemiBase = new THREE.Color();              // its built (warm) sky colour, restored + re-lerped each frame
  private _hcPal = cityPaletteAt(0.5);                     // palette scratch → the interior nightFactor (same curve as the exterior)
  /** ExteriorVista — the offscreen 3D sea diorama + the portal target the glass samples. Built once in the
   *  constructor (procedural, no async), then rendered to `_vistaRT` each play-mode frame with the live camera. */
  private _vistaScene: THREE.Scene | null = null;         // the offscreen exterior world (ocean/sky/islands/…)
  private _vistaRT: THREE.WebGLRenderTarget | null = null; // portal target the window glass samples by screen-UV
  private _vistaOceanMat: THREE.ShaderMaterial | null = null; // Gerstner sea (uTime/uCam/day-night uniforms)
  private _vistaSkyMat: THREE.ShaderMaterial | null = null;   // gradient sky/horizon backdrop
  private _vistaSky: THREE.Mesh | null = null;            // the backdrop mesh (follows the camera in XZ)
  private _vistaSun: THREE.Sprite | null = null;          // soft sun/moon glow near the horizon
  private _vistaIslands: THREE.Object3D | null = null;    // instanced distant island silhouettes (world-fixed)
  private _vistaShip: THREE.Sprite | null = null;         // the lone ship crossing the far horizon
  private _vistaGulls: THREE.Sprite[] = [];               // 0–5 rare gulls (off on Low)
  private _glassMat: THREE.ShaderMaterial | null = null;  // window pane material: samples _vistaRT + glass tint
  private _vistaPal = { skyTop: new THREE.Color(), skyHaze: new THREE.Color(), seaDeep: new THREE.Color(), seaShallow: new THREE.Color(), sun: new THREE.Color(), sunI: 1, sunY: 0.5, star: 0 };
  private _vistaQuality: GraphicsPreset = getSetting('graphics'); // low = portal off (cheap gradient glass)
  private _vistaT = 0;                                     // latest render time (seconds) for the portal pass
  private _vistaLight: THREE.DirectionalLight | null = null; // subtle cool sea-light raking into the interior
  private _godRays: THREE.Mesh | null = null;             // additive light shafts from the stern windows
  private _godRayMat: THREE.MeshBasicMaterial | null = null;
  private _dust: THREE.Points | null = null;              // slow motes drifting inside the shafts
  private _dustVel: Float32Array | null = null;
  private _candleFlame: THREE.InstancedMesh | null = null; // ONE instanced flame across all candles + lamps
  private _candleHalo: THREE.InstancedMesh | null = null;  // its soft additive halo
  private _candleMat: THREE.MeshStandardMaterial | null = null;
  private _flick = 0;                                      // smoothed organic flicker 0..1 (drives hearth+candles)
  private _phaseOverride: number | null = null;           // E2E: pin the interior time-of-day (render-only)
  private _sitting = false;
  private _sitAnchor: SeatSpot | null = null;
  private _sitFrom: { x: number; y: number } | null = null; // where the player stood when they sat (auto-stand on walk)
  private _yourId = -1;
  /** In-world editor hooks (Onda Editor-em-Mundo). `_editorFreeCam` hands the camera to the editor's OrbitControls
   *  (updateFollowCamera becomes a no-op); `_editorSit` (inherited, protected from PortScene) forces the ghost avatar
   *  into an arbitrary configured seat for the live Seat-config preview, bypassing nearestSeat + the walk-away
   *  auto-stand. Both inert in-game. */
  private _editorFreeCam = false;

  constructor(renderer: Renderer) {
    super(renderer);
    // HouseScene fields are only touched AFTER super() — the furniture/trophy layers attach here (the shell
    // was already built by super() via the overridden buildStaticWorld).
    this.furnitureRoot = new THREE.Group();
    this.slotRoot = new THREE.Group();
    this.scene.add(this.furnitureRoot);
    this.scene.add(this.hearthRoot);
    this.scene.add(this.slotRoot);
    // ExteriorVista: build the offscreen sea diorama + swap the glass onto the stern panes NOW (procedural, no
    // async) so the windows are alive from the very first frame — independent of the GLB/texture streaming below.
    this.buildVista();
    onSettingChange((s) => this.setVistaQuality(s.graphics)); // live Low/Medium/High from Settings › Graphics
  }

  /* ------------------------------- the cabin shell ------------------------------- */

  /** Build the 22×14 timber cabin into staticRoot. Runs during super() BEFORE HouseScene fields exist, so it
   *  touches ONLY inherited members (staticRoot/tiledTex/addTorch/finalizeTorches) + module constants. */
  protected override buildStaticWorld(): void {
    const W = HOUSE.WIDTH, D = HOUSE.DEPTH;
    this.scene.background = new THREE.Color(0x1a1410);
    this.scene.fog = new THREE.Fog(0x120d0a, 22, 60);

    // warm interior lighting: hemisphere + fill + a couple of point lights (hearth + centre). Tuned brighter so
    // the cabin reads as a warm, CLEARLY-lit captain's quarters on mobile — a cozy amber floor that never falls to
    // breu. Combined with the softer night cool-cast (updateInterior), the interior stays legible at every time of
    // day; the hearth/candles keep it warm while the windows still darken to night behind the glass.
    this.staticRoot.add(new THREE.HemisphereLight(0x786036, 0x22150a, 1.15));
    this.staticRoot.add(new THREE.AmbientLight(0x483827, 0.72));
    const key = new THREE.PointLight(0xffdca0, 1.25, 46, 1.2); key.position.set(W / 2, 3.0, D / 2); this.staticRoot.add(key);
    const hearth = new THREE.PointLight(0xff8c2a, 1.6, 16, 1.6); hearth.position.set(HOUSE_SHELL.hearth.x, 1.2, HOUSE_SHELL.hearth.y); hearth.userData.hearthLight = 1; this.staticRoot.add(hearth);

    const timber = new THREE.MeshStandardMaterial({ color: C.timber, roughness: 1 });
    // WALLS get the SAME lit weathered-plank texture as the floor (map × white, roughness 1). The old walls were a
    // near-BLACK solid tint (C.timber 0x38271a): a vertical face gets almost nothing from the up-biased hemisphere +
    // the dark-brown ambient, so `albedo(≈0.04) × irradiance(≈0.04)` crushed to pure black under ACES — the cabin
    // read as a black VOID with the trophies/dust-covers floating in it (the "não tá renderizado" bug: the wall
    // and the woodDark shelf mounts both vanished, leaving only the pale labels + dust domes). A textured wall
    // catches the exact same light the floor does, so the trophy wall reads as timber and the displays sit ON it.
    const wallMat = new THREE.MeshStandardMaterial({ map: this.tiledTex('/textures/dock_planks.webp', 5, 2.4), roughness: 1 });

    // plank floor (real weathered planks)
    const floor = new THREE.Mesh(new THREE.BoxGeometry(W, 0.3, D), new THREE.MeshStandardMaterial({ map: this.tiledTex('/textures/dock_planks.webp', W / 2.2, D / 2.2), roughness: 1 }));
    floor.position.set(W / 2, -0.15, D / 2); this.staticRoot.add(floor);
    // ceiling + beams. This is the "roof": our cameras always sit ABOVE the walls (third-person follow at
    // ~4.2 m, decorate at 15 m), so an opaque ceiling would only ever occlude the interior. Tagged roof=1 and
    // culled every frame by applyRoofCull (Sims/Clash "no roof over the play view") — the interior reads from
    // inside while a hypothetical low eye-level cam would still see it re-seal.
    const ceil = new THREE.Mesh(new THREE.BoxGeometry(W, 0.3, D), new THREE.MeshStandardMaterial({ color: 0x241811, roughness: 1 }));
    ceil.position.set(W / 2, WALL_H + 0.15, D / 2); ceil.userData.roof = 1; this.staticRoot.add(ceil);
    // ceiling beams (part of the roof — hidden with the ceiling so they never streak across the top-down view)
    for (let i = 1; i < 5; i++) { const beam = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.4, D), timber); beam.position.set((W / 5) * i, WALL_H - 0.2, D / 2); beam.userData.roof = 1; this.staticRoot.add(beam); }

    // walls (thick timber). South wall (y≈0) is split around the door gap [exit.minX..maxX].
    const wall = (w: number, h: number, d: number, x: number, y: number, z: number): void => { const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), wallMat); m.position.set(x, y, z); this.staticRoot.add(m); };
    wall(0.3, WALL_H, D, 0.15, WALL_H / 2, D / 2);        // west
    wall(0.3, WALL_H, D, W - 0.15, WALL_H / 2, D / 2);    // east
    const ex = HOUSE_SHELL.exit;
    const leftW = ex.minX, rightW = W - ex.maxX;
    if (leftW > 0.1) wall(leftW, WALL_H, 0.3, leftW / 2, WALL_H / 2, 0.15);
    if (rightW > 0.1) wall(rightW, WALL_H, 0.3, ex.maxX + rightW / 2, WALL_H / 2, 0.15);
    // door frame over the gap
    const frameMat = new THREE.MeshStandardMaterial({ color: C.wood, roughness: 1 });
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(ex.maxX - ex.minX + 0.6, 0.9, 0.4), frameMat); lintel.position.set((ex.minX + ex.maxX) / 2, WALL_H - 0.45, 0.15); this.staticRoot.add(lintel);

    // north wall = the galleon STERN, with tall glowing windows onto an EPIC seascape (a painting-style view
    // chosen by the time of day — see preloadInterior). Solid piers between the panes. The five panes each show
    // a horizontal SLICE of one wide panorama (UVs sliced below) so together they read as one continuous view;
    // the recessed glass sits BEHIND the mullions + frame rails so it never reads as a flat pasted poster.
    wall(W, WALL_H, 0.3, W / 2, WALL_H / 2, D - 0.15);
    const skyTex = this.sternTex();
    const winMat = new THREE.MeshStandardMaterial({ map: skyTex, emissive: 0x6f97b5, emissiveMap: skyTex, emissiveIntensity: 0.7, roughness: 0.5 });
    const panes = 5;
    for (let i = 0; i < panes; i++) {
      const px = (W / panes) * (i + 0.5);
      const pg = new THREE.BoxGeometry(W / panes - 0.7, 1.9, 0.12);
      const uv = pg.attributes.uv as THREE.BufferAttribute; // slice this pane to panorama column [i/panes,(i+1)/panes]
      for (let v = 0; v < uv.count; v++) uv.setX(v, (i + uv.getX(v)) / panes);
      uv.needsUpdate = true;
      const pane = new THREE.Mesh(pg, winMat); pane.userData.window = i;
      pane.position.set(px, 1.9, D - 0.28); this.staticRoot.add(pane);            // recessed glass
      const mullion = new THREE.Mesh(new THREE.BoxGeometry(0.2, 2.2, 0.26), frameMat); mullion.position.set((W / panes) * (i + 1), 1.9, D - 0.42); this.staticRoot.add(mullion);
    }
    // chunky top + sill rails so the whole stern reads as one framed window (frame in FRONT of the glass)
    const railTop = new THREE.Mesh(new THREE.BoxGeometry(W - 0.4, 0.3, 0.28), frameMat); railTop.position.set(W / 2, 3.0, D - 0.42); this.staticRoot.add(railTop);
    const railBot = new THREE.Mesh(new THREE.BoxGeometry(W - 0.4, 0.34, 0.3), frameMat); railBot.position.set(W / 2, 0.9, D - 0.42); this.staticRoot.add(railBot);
    // a soft stern glow so the windows read as light
    const sternGlow = new THREE.PointLight(0x8fb8d8, 0.6, 26, 1.5); sternGlow.position.set(W / 2, 2.0, D - 1.5); this.staticRoot.add(sternGlow);

    // NE corner is now flat, walkable floor. The raised mezzanine deck + its stair "hint" + support posts were
    // REMOVED (the walker is 2D and never climbed the platform — it read as a provocation that blocked but didn't
    // ascend). Collision followed: HOUSE_SHELL.solidRects + obstacles are now empty, so sim + houseRoom + the
    // client prediction all agree the corner is plain floor. two-story house = onda futura.

    // hearth mantel at HOUSE_SHELL.hearth (a stone-ish nook so the corner reads "home"). Tagged hearthProc:
    // the iron-stove GLB (preloadInterior) is placed on the same footprint and these hide when it lands — but
    // they stay as a warm procedural fallback until (or unless) the GLB streams in.
    const hx = HOUSE_SHELL.hearth.x, hy = HOUSE_SHELL.hearth.y; void hy;
    const stone = new THREE.MeshStandardMaterial({ color: 0x5a5148, roughness: 1 });
    const mantel = new THREE.Mesh(new THREE.BoxGeometry(2.2, 2.0, 0.6), stone); mantel.position.set(hx, 1.0, D - 0.5); mantel.userData.hearthProc = 1; this.staticRoot.add(mantel);
    const emberMat = new THREE.MeshStandardMaterial({ color: 0xff7a1a, emissive: 0xff7a1a, emissiveIntensity: 1.4, roughness: 0.6 });
    const embers = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.4, 0.3), emberMat); embers.position.set(hx, 0.35, D - 0.55); embers.userData.hearthProc = 1; embers.userData.hearthEmber = 1; this.staticRoot.add(embers);

    // a captain's pennant hanging near the mezzanine — decorative shell cloth that sways (see clothSwayMaterial).
    const banner = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 1.7, 4, 8), this.clothSwayMaterial());
    banner.geometry.translate(0, -0.85, 0); // pivot at the top rod so the sway hinges from the hanging point
    banner.position.set(W - 0.5, WALL_H - 0.35, D - 4.5); banner.rotation.y = -Math.PI / 2; this.staticRoot.add(banner);
    const bpole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.0, 6), new THREE.MeshStandardMaterial({ color: C.iron, roughness: 0.6 }));
    bpole.rotation.x = Math.PI / 2; bpole.position.set(W - 0.42, WALL_H - 0.35, D - 4.5); this.staticRoot.add(bpole);

    // wall lanterns (reuse the port's instanced brazier/flame path) + a couple by the door
    this.addTorch(1.2, D - 1.2, 1.0);
    this.addTorch(W - 1.2, D - 1.2, 1.0);
    this.addTorch(ex.minX - 0.6, 1.4, 1.0);
    this.addTorch(ex.maxX + 0.6, 1.4, 1.0);
    this.finalizeTorches();
  }

  /** A cheap sea/sky gradient for the stern windows (canvas → texture; cached under a fake url key). */
  private sternTex(): THREE.Texture {
    const cv = document.createElement('canvas'); cv.width = 32; cv.height = 128;
    const ctx = cv.getContext('2d')!;
    const g = ctx.createLinearGradient(0, 0, 0, 128);
    g.addColorStop(0, '#bfe0f2'); g.addColorStop(0.55, '#7fb4d6'); g.addColorStop(0.6, '#2c6d84'); g.addColorStop(1, '#1b4a5c');
    ctx.fillStyle = g; ctx.fillRect(0, 0, 32, 128);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; tex.needsUpdate = true;
    return tex;
  }

  /* ------------------------------- Interior Vivo: living light + ambience ------------------------------- */

  /** A cloth material whose lower vertices sway on a shared clock (curtains + the shell banner). The rod/top
   *  (pivot) barely moves; the hem billows. Per-instance phase comes from the instance position so a row of
   *  curtains doesn't sway in lockstep. Behaves like the ships' sail wave — a gentle vertex displacement. */
  private clothSwayMaterial(pivotY = 0, amp = 0.16, vertexColors = false, color = C.cloth): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({ color: vertexColors ? 0xffffff : color, vertexColors, roughness: 0.92, metalness: 0, side: THREE.DoubleSide });
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uSwayTime = SWAY_TIME;
      shader.uniforms.uPivotY = { value: pivotY };
      shader.uniforms.uAmp = { value: amp };
      shader.vertexShader = 'uniform float uSwayTime; uniform float uPivotY; uniform float uAmp;\n' +
        shader.vertexShader.replace('#include <begin_vertex>', `#include <begin_vertex>
        float hang = max(0.0, uPivotY - position.y);
        float ph = position.x * 1.3;
        #ifdef USE_INSTANCING
          ph += instanceMatrix[3].x * 0.7 + instanceMatrix[3].z * 0.9;
        #endif
        transformed.x += sin(uSwayTime * 1.7 + ph) * uAmp * hang;
        transformed.z += cos(uSwayTime * 1.25 + ph * 0.7) * uAmp * 0.45 * hang;`);
    };
    mat.customProgramCacheKey = () => 'cc-clothsway';
    return mat;
  }

  /** A soft radial dot (for dust motes + candle halos), cached under a fake url key. */
  private softDotTex(): THREE.Texture {
    const cv = document.createElement('canvas'); cv.width = cv.height = 64;
    const ctx = cv.getContext('2d')!;
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, 'rgba(255,255,255,0.82)'); g.addColorStop(0.4, 'rgba(255,246,220,0.4)'); g.addColorStop(1, 'rgba(255,240,210,0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(cv); tex.needsUpdate = true; return tex;
  }

  /** Onda 8 preload also streams the interior hero GLBs + the stern seascapes + builds the god-ray/dust layers. */
  override async preload(): Promise<void> {
    await super.preload();
    void this.preloadInterior();
  }

  /** Load + bake the iron-stove & candelabrum GLB heroes, the three stern seascapes, and build the fixed
   *  hearth + light shafts. Idempotent; each piece degrades to the procedural fallback on failure. */
  private async preloadInterior(): Promise<void> {
    if (this.interiorLoading || this.interiorReady) return;
    this.interiorLoading = true;
    try {
      const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/libs/meshopt_decoder.module.js'),
      ]);
      const loader = new GLTFLoader() as unknown as { loadAsync(u: string): Promise<{ scene: THREE.Object3D }>; setMeshoptDecoder(d: unknown): void };
      loader.setMeshoptDecoder(MeshoptDecoder);
      const [stove, cand, card, globe, aqua, chart, book, jelly, rum, spy] = await Promise.all([
        this.bakeGlbFit(loader, STOVE_URL, FurnitureKind.IRON_STOVE, { color: 0xff8c2a, intensity: 1.1, dist: 9, y: 1.0 }),
        this.bakeGlbFit(loader, CANDELABRUM_URL, FurnitureKind.CANDELABRA, { color: 0xffcf7a, intensity: 0.55, dist: 5, y: 1.25 }),
        this.bakeGlbFit(loader, CARD_TABLE_URL, FurnitureKind.CARD_TABLE),
        this.bakeGlbFit(loader, GLOBE_URL, FurnitureKind.GLOBE),
        this.bakeGlbFit(loader, AQUARIUM_URL, FurnitureKind.AQUARIUM, { color: 0x4fd0ff, intensity: 0.5, dist: 5, y: 1.0 }),
        // Leva 3 (móveis do dono). Jelly Strange Lamp gets a soft teal point-light for a subtle room glow on top
        // of its baked emissive; the jelly-shrine, spyglass tripod + rum workbench ALL read short if fit to a
        // 1-cell footprint, so a fitScale lets them stand ~1.5 m tall (collision stays the declared footprint —
        // the overhang is cosmetic). The Strange Lamp shipped at fit=1 (≈1.0 m, dwarfed next to a 1.8 m captain);
        // 1.45 brings it into line with its sibling 1-cell heroes (rum 1.5 m, spyglass 1.48 m).
        this.bakeGlbFit(loader, CHART_TABLE_URL, FurnitureKind.CHART_TABLE),
        this.bakeGlbFit(loader, BOOKSHELF_URL, FurnitureKind.BOOKSHELF),
        this.bakeGlbFit(loader, STRANGE_LAMP_URL, FurnitureKind.STRANGE_LAMP, { color: 0x36e0d0, intensity: 0.8, dist: 6, y: 1.15 }, 1.45),
        this.bakeGlbFit(loader, RUM_BARREL_URL, FurnitureKind.DECO_BARREL, undefined, 1.4),
        this.bakeGlbFit(loader, SPYGLASS_URL, FurnitureKind.SPYGLASS, undefined, 1.35),
      ]);
      this.stoveBake = stove; this.candelabrumBake = cand;
      this.cardTableBake = card; this.globeBake = globe; this.aquariumBake = aqua;
      this.chartTableBake = chart; this.bookshelfBake = book; this.strangeLampBake = jelly;
      this.rumBarrelBake = rum; this.spyglassBake = spy;
      // (the stern windows are the live ExteriorVista, built in the constructor — no seascape textures to stream)
      this.placeHearthStove();
      this.buildGodRaysAndDust();
      this.interiorReady = true;
      this.rebuildFurniture(); // swap procedural stove/candelabra for the GLB heroes + (re)seat candle flames
    } catch (err) { console.warn('[house] interior polish load failed', err); }
  }

  /** Normalize a loaded GLB hero material to the house's robust matte profile — the ONE place every interior GLB
   *  (bakeGlbFit + the berth) passes through, so a bad Meshy export can never diverge again.
   *
   *  WHY (the móveis-invisíveis bug): the leva-3 re-optimization (skull_bed/chart_table/strange_lamp/bookshelf/
   *  rum_barrel/spyglass) shipped Meshy's RAW PBR — `metalness=1`, `roughness=1`, `emissive=#ffffff` + an ORM/
   *  emissive map — whereas every other asset in the game is `metalness=0, roughness=0.8, emissive=#000000`. That
   *  makes the piece's whole look HINGE on its (embedded webp) maps decoding: where a device can't decode the
   *  webp (some mobile Safari `createImageBitmap` paths), the emissive falls back to full-white → the piece renders
   *  as a FEATURELESS WHITE BLOB, while the robust matte assets degrade to a plain lit solid ("só os antigos
   *  renderizam"). We force the safe profile: never metallic (no envmap in here anyway → metal reads black), and
   *  never fullbright-white when the emissive map is absent (a present emissive map — the Strange Lamp jelly — is
   *  kept, so its glow survives). DoubleSide so thin faces (globe cradle, tank glass, felt rim) hold at grazing
   *  angles (the leva-1 lesson). */
  private normalizeFurnMat(mat: THREE.Material): void {
    mat.side = THREE.DoubleSide;
    const std = mat as THREE.MeshStandardMaterial;
    if (typeof std.metalness === 'number') {
      std.metalness = 0;
      if (std.roughness > 0.95) std.roughness = 0.85; // Meshy default 1.0 → match the family
      if (std.emissive && !std.emissiveMap) std.emissive.setRGB(0, 0, 0);
    }
    mat.needsUpdate = true;
  }

  /** Bake one GLB into a single instanceable geometry (merged, position/normal/uv only) fit to a furniture
   *  kind's cell footprint — the same recipe as the captain berth. Returns a FurnBake with an optional light. */
  private async bakeGlbFit(loader: { loadAsync(u: string): Promise<{ scene: THREE.Object3D }> }, url: string, kind: number, light?: FurnBake['light'], fitScale = 1): Promise<FurnBake | null> {
    try {
      const gltf = await loader.loadAsync(url);
      const obj = gltf.scene; obj.position.set(0, 0, 0); obj.rotation.set(0, 0, 0); obj.scale.set(1, 1, 1); obj.updateMatrixWorld(true);
      const geos: THREE.BufferGeometry[] = []; let mat: THREE.Material | null = null;
      obj.traverse((o) => {
        const m = o as THREE.Mesh; if (!m.isMesh || !m.geometry) return;
        const g = m.geometry.clone();
        for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'normal' && name !== 'uv') g.deleteAttribute(name);
        g.morphAttributes = {}; g.morphTargetsRelative = false; // drop morph data so a multi-mesh GLB (e.g. a sail-morph hull) merges (see preloadShipModel)
        if (!g.attributes.normal) g.computeVertexNormals();
        if (!g.attributes.uv) { const n = g.attributes.position!.count; g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2)); }
        g.applyMatrix4(m.matrixWorld); geos.push(g); if (!mat) mat = Array.isArray(m.material) ? m.material[0]! : m.material;
      });
      if (!geos.length || !mat) return null;
      this.normalizeFurnMat(mat);
      const merged = geos.length === 1 ? geos[0]! : (mergeGeometries(geos, false) ?? geos[0]!);
      const bbox = new THREE.Box3().setFromBufferAttribute(merged.attributes.position as THREE.BufferAttribute);
      const size = bbox.getSize(new THREE.Vector3());
      const foot = furnitureById(kind)!.foot;
      // Fit to the cell footprint (min ratio); fitScale (>1) lets a tall/protruding hero (spyglass tripod, rum
      // workbench) read at a natural height while its collision stays the declared footprint (overhang is fine).
      const scale = Math.min(foot.w / (size.x || 1), foot.d / (size.z || 1)) * fitScale;
      return { geo: merged, mat, isGlb: true, scale, groundY: -bbox.min.y * scale, light };
    } catch (err) { console.warn('[house] GLB bake failed', url, err); return null; }
  }

  /* ------------------------------- ExteriorVista (the living sea) ------------------------------- */

  // Near/far Z (world) the sea sheet spans — the near edge is the stern wall, the far edge is where it hazes out.
  private static readonly VNEAR_Z = HOUSE.DEPTH;                          // ≈14 m — the stern wall plane
  private static readonly VFAR_Z = VISTA.oceanCenterZ + VISTA.oceanSpanZ / 2;
  private _vScratchCam = new THREE.Vector3();
  private _vScratchSun = new THREE.Vector3();

  /** Build the whole ExteriorVista: an offscreen 3D scene (ocean + sky/horizon backdrop + sun + distant
   *  islands/ship/gulls) and its render target, then swap the see-through glass onto the stern panes. Called
   *  once from the constructor — fully procedural, no async, so the windows are alive on the first frame. The
   *  solid stern wall masks it: the glass is the only opening, so nothing can leak past a wall in any camera. */
  private buildVista(): void {
    if (this._vistaScene) return;
    const scene = new THREE.Scene();
    this._vistaScene = scene;
    this.vistaPaletteAt(this.interiorPhase(), true); // seed the palette scratch

    this.buildVistaOcean(scene);
    this.buildVistaSky(scene);
    this.buildVistaDistants(scene);

    // the portal target the glass samples (sized in setVistaQuality); glass material swapped onto the panes.
    this.buildGlassMaterial();
    this._ensureLiveRefs();
    for (const p of this._winPanes ?? []) { const old = p.material as THREE.Material; p.material = this._glassMat!; if (old && !Array.isArray(old)) old.dispose(); }

    // a subtle COOL directional fill raking in from the stern — the "outside light" touching the interior. Kept
    // gentle (daylight-scaled) so the warm hearth/ambient balance (hemi 1.15 / ambient 0.72) is never overpowered.
    const dl = new THREE.DirectionalLight(0xbcd8f0, 0.0);
    dl.position.set(HOUSE.WIDTH / 2, 3.4, HOUSE.DEPTH + 6); dl.target.position.set(HOUSE.WIDTH / 2, 1.0, HOUSE.DEPTH / 2);
    this.staticRoot.add(dl); this.staticRoot.add(dl.target); this._vistaLight = dl;

    this.setVistaQuality(this._vistaQuality); // create the RT + apply tiered feature toggles
  }

  /** The near ocean: one big Gerstner sheet (world-fixed) reusing the game's shared OCEAN_WAVES via
   *  buildOceanGLSL — so the cabin sea rides the exact same wave language as the open sea. Stylized fragment
   *  (deep→shallow gradient, Fresnel sky-reflection, sun glitter, crest foam) that dissolves into the horizon
   *  haze at its far edge so the sea meets the sky with no visible seam. Writes stylized sRGB (non-PBR). */
  private buildVistaOcean(scene: THREE.Scene): void {
    const geo = this.makeOceanGeo(VISTA.oceanSubdiv[this._vistaQuality]);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 }, uAmp: { value: VISTA.oceanWaveAmp }, uCam: { value: new THREE.Vector3() },
        uSeaDeep: { value: this._vistaPal.seaDeep }, uSeaShallow: { value: this._vistaPal.seaShallow },
        uHaze: { value: this._vistaPal.skyHaze }, uSun: { value: this._vistaPal.sun },
        uSunDir: { value: new THREE.Vector3(0, 0.4, 1).normalize() }, uSunI: { value: 1 },
        // the generated ocean GLSL references these (see oceanEnvUniforms). The cabin vista is a painted
        // backdrop with no live wind of its own, so it holds them at the IDENTITY sea for ever — but they
        // must still exist, or uChopDir reads as (0,0) and the chop band becomes a pumping plane.
        ...oceanEnvUniforms(),
      },
      vertexShader: `
        uniform float uTime; uniform float uAmp;
        varying vec3 vWorld; varying vec3 vNormal; varying float vH; varying float vDistF;
        ${buildOceanGLSL()}
        void main(){
          vec3 wp = position;                                  // geometry is baked in WORLD space (mesh at origin)
          float sc = ${VISTA.oceanScale.toFixed(3)};
          vec2 p = wp.xz / sc;                                 // widen the wavelength for a painted look
          vec3 d = oceanSample(p, uTime, uAmp);
          wp.x += d.x; wp.z += d.z; wp.y += d.y;
          // smooth analytic normal from the height slope (two cheap neighbour samples → no faceted flat-shading)
          float E = 0.6;
          float hx = oceanSample((wp.xz + vec2(E, 0.0)) / sc, uTime, uAmp).y - d.y;
          float hz = oceanSample((wp.xz + vec2(0.0, E)) / sc, uTime, uAmp).y - d.y;
          vNormal = normalize(vec3(-hx / E, 1.0, -hz / E));
          vWorld = wp; vH = d.y;
          vDistF = clamp((wp.z - ${HouseScene.VNEAR_Z.toFixed(2)}) / ${(HouseScene.VFAR_Z - HouseScene.VNEAR_Z).toFixed(2)}, 0.0, 1.0);
          gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
        }`,
      fragmentShader: `
        precision highp float;
        uniform vec3 uCam, uSeaDeep, uSeaShallow, uHaze, uSun, uSunDir; uniform float uSunI, uAmp;
        varying vec3 vWorld; varying vec3 vNormal; varying float vH; varying float vDistF;
        void main(){
          vec3 n = normalize(vNormal);
          vec3 view = normalize(uCam - vWorld);
          float fres = pow(1.0 - max(dot(n, view), 0.0), 4.0);
          vec3 col = mix(uSeaDeep, uSeaShallow, clamp(0.5 + vH * 1.1, 0.0, 1.0)); // trough→crest colour contrast
          col = mix(col, uHaze, fres * 0.20);                                   // grazing sky reflection (subtle — keep the teal)
          vec3 hlf = normalize(uSunDir + view);                                 // sun glitter on the crests (tight + calm)
          col += uSun * pow(max(dot(n, hlf), 0.0), 90.0) * uSunI * 0.6;
          float foam = smoothstep(0.62, 0.98, vH / max(uAmp, 0.01));            // discreet crest foam only
          col = mix(col, vec3(0.9, 0.95, 1.0), foam * 0.4);
          vec3 seaHz = mix(uSeaShallow, uHaze, 0.5);                            // richer teal-grey horizon (not a white wash)
          col = mix(col, seaHz, smoothstep(${VISTA.horizonHaze.toFixed(2)}, 0.9, vDistF)); // dissolve into horizon band
          gl_FragColor = vec4(col, 1.0);
        }`,
      fog: false, toneMapped: false,
    });
    const mesh = new THREE.Mesh(geo, mat); mesh.frustumCulled = false; mesh.renderOrder = 0;
    scene.add(mesh); this._vistaOceanMat = mat;
  }

  /** A world-space ocean plane baked into the XZ plane (mesh stays at origin, verts carry world coords). */
  private makeOceanGeo(subdiv: number): THREE.BufferGeometry {
    const g = new THREE.PlaneGeometry(VISTA.oceanSpanX, VISTA.oceanSpanZ, subdiv, Math.round(subdiv * VISTA.oceanSpanZ / VISTA.oceanSpanX));
    g.rotateX(-Math.PI / 2);                                     // lie flat in XZ
    g.translate(HOUSE.WIDTH / 2, VISTA.seaLevel, VISTA.oceanCenterZ);
    return g;
  }

  /** The far sky/horizon backdrop: a big inverted sphere (BackSide) that follows the camera in XZ so it always
   *  backs the windows. A vertical gradient (sea-haze below the horizon → haze band → sky top) with slow drifting
   *  clouds. It sits far behind the near sea, so moving the camera parallaxes the near swell against this backdrop. */
  private buildVistaSky(scene: THREE.Scene): void {
    const geo = new THREE.SphereGeometry(VISTA.backdropRadius, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide, fog: false, toneMapped: false, depthWrite: false,
      uniforms: {
        uSkyTop: { value: this._vistaPal.skyTop }, uSkyHaze: { value: this._vistaPal.skyHaze },
        uSeaHaze: { value: this._vistaPal.seaDeep }, uCloud: { value: this.cloudTex() },
        uCloudT: { value: 0 }, uStar: { value: 0 }, uSun: { value: this._vistaPal.sun }, uSunDir: { value: new THREE.Vector3() },
      },
      vertexShader: 'varying vec3 vDir; varying vec2 vUv; void main(){ vDir = normalize(position); vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: `
        precision highp float;
        uniform vec3 uSkyTop, uSkyHaze, uSeaHaze, uSun, uSunDir; uniform sampler2D uCloud; uniform float uCloudT, uStar;
        varying vec3 vDir; varying vec2 vUv;
        float hash(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5); }
        void main(){
          float el = vDir.y;                                             // -1 (down) … 1 (up)
          // the cabin's follow camera looks only slightly down, so the stern windows frame just below the true
          // horizontal — paint the horizon band a touch BELOW el=0 so a clear sky reads in the upper window.
          vec3 sky = mix(uSkyHaze, uSkyTop, smoothstep(-0.04, 0.5, el));  // haze band at horizon → deep top
          vec3 col = mix(uSeaHaze, sky, smoothstep(-0.16, -0.03, el));    // below the horizon band → sea-haze
          float sun = max(dot(vDir, uSunDir), 0.0);                      // warm glow bloom around the sun
          col += uSun * pow(sun, 10.0) * 0.35 + uSun * pow(sun, 220.0) * 0.9;
          float cl = texture2D(uCloud, vUv * ${VISTA.cloudScale.toFixed(2)} + vec2(uCloudT, 0.0)).r; // slow drift
          cl *= smoothstep(-0.08, 0.25, el);                             // clouds fade in above the horizon band
          col = mix(col, mix(uSkyTop, vec3(1.0), 0.4), cl * 0.55);
          if (uStar > 0.01){ float s = step(0.995, hash(floor(vDir.xy * 220.0))); col += vec3(s) * uStar * smoothstep(-0.02, 0.3, el) * 0.6; }
          gl_FragColor = vec4(col, 1.0);
        }`,
    });
    const mesh = new THREE.Mesh(geo, mat); mesh.frustumCulled = false; mesh.renderOrder = -1;
    scene.add(mesh); this._vistaSky = mesh; this._vistaSkyMat = mat;
    scene.background = this._vistaPal.skyHaze; // any pane pixel with no geometry still reads as horizon haze
  }

  /** Distant life: a lone ship crossing the far horizon, 0–5 rare gulls, and a soft sun/moon glow — all cheap
   *  billboards, all tier-gated in setVistaQuality. Islands are built lazily (instanced) by setVistaQuality. */
  private buildVistaDistants(scene: THREE.Scene): void {
    const sun = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.softDotTex(), color: 0xfff2d0, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false, fog: false, toneMapped: false }));
    sun.scale.setScalar(26); sun.renderOrder = 0; scene.add(sun); this._vistaSun = sun;

    const ship = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.shipSilTex(), color: 0x1b2b33, transparent: true, opacity: 0.9, depthWrite: false, fog: false, toneMapped: false }));
    ship.scale.set(7, 2.6, 1); ship.center.set(0.5, 0); scene.add(ship); this._vistaShip = ship;

    // gulls: a handful of tiny dark billboards doing slow lazy arcs (positions seeded once, animated in updateVista).
    for (let i = 0; i < 5; i++) {
      const gull = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.gullTex(), color: 0x2a3238, transparent: true, opacity: 0.7, depthWrite: false, fog: false, toneMapped: false }));
      gull.scale.set(1.5, 0.7, 1); gull.userData.seed = i * 1.7; scene.add(gull); this._vistaGulls.push(gull);
    }
  }

  /** The window-glass portal material: samples the offscreen vista by SCREEN-space UV (true see-through with
   *  real parallax) when the portal is on; else a cheap day/night gradient (Low tier / decorate). A subtle
   *  Fresnel tint + faint interior reflection reads as glass — never a mirror, never hiding the sea. */
  private buildGlassMaterial(): void {
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uRT: { value: null }, uPortal: { value: 0 }, uCam: { value: new THREE.Vector3() },
        uTint: { value: new THREE.Color(VISTA.glassTint) }, uOpacity: { value: VISTA.glassOpacity }, uReflect: { value: VISTA.glassReflection },
        uSkyTop: { value: this._vistaPal.skyTop }, uSkyHaze: { value: this._vistaPal.skyHaze }, uSeaDeep: { value: this._vistaPal.seaDeep }, uSeaShallow: { value: this._vistaPal.seaShallow },
        uInterior: { value: new THREE.Color(0xffcf94) }, uGlow: { value: 0.5 },
      },
      vertexShader: `
        varying vec4 vClip; varying vec3 vWorld; varying float vLocalY;
        void main(){ vLocalY = uv.y; vec4 wp = modelMatrix * vec4(position, 1.0); vWorld = wp.xyz; vClip = projectionMatrix * viewMatrix * wp; gl_Position = vClip; }`,
      fragmentShader: `
        precision highp float;
        uniform sampler2D uRT; uniform float uPortal, uOpacity, uReflect, uGlow;
        uniform vec3 uCam, uTint, uSkyTop, uSkyHaze, uSeaDeep, uSeaShallow, uInterior;
        varying vec4 vClip; varying vec3 vWorld; varying float vLocalY;
        vec3 lin2srgb(vec3 c){ return mix(1.055 * pow(max(c, 0.0), vec3(0.41666)) - 0.055, c * 12.92, step(c, vec3(0.0031308))); }
        void main(){
          vec3 sea;
          if (uPortal > 0.5){
            vec2 uv = vClip.xy / vClip.w * 0.5 + 0.5;                    // screen-UV = the vista pixel behind this pane
            sea = texture2D(uRT, uv).rgb;
          } else {
            float horizon = 0.62;                                        // cheap fallback: sky over sea gradient
            vec3 sky = mix(uSkyHaze, uSkyTop, smoothstep(horizon, 1.0, vLocalY));
            vec3 water = mix(uSeaDeep, uSeaShallow, smoothstep(0.0, horizon, vLocalY));
            sea = mix(water, sky, smoothstep(horizon - 0.03, horizon + 0.03, vLocalY));
          }
          vec3 view = normalize(uCam - vWorld);
          float fres = pow(1.0 - max(view.z * -1.0, 0.0), 4.0);          // pane faces -Z (south) into the room
          vec3 col = mix(sea, uTint, uOpacity);                          // faint blue-green glass tint
          col += uInterior * fres * uReflect * uGlow;                    // faint warm interior reflection (never a mirror)
          gl_FragColor = vec4(lin2srgb(col), 1.0);                       // encode ONCE here (linear vista → sRGB screen)
        }`,
      fog: false, toneMapped: false,
    });
    this._glassMat = mat;
  }

  /* ---- procedural vista textures (canvas → sprite/backdrop; no external art) ---- */

  /** Soft layered cloud noise for the sky backdrop (wraps horizontally so the slow drift never shows a seam). */
  private cloudTex(): THREE.Texture {
    const s = 256, cv = document.createElement('canvas'); cv.width = cv.height = s; const ctx = cv.getContext('2d')!;
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, s, s);
    for (let i = 0; i < 40; i++) {
      const cx = Math.random() * s, cy = (0.15 + Math.random() * 0.5) * s, r = 18 + Math.random() * 46;
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      const a = 0.12 + Math.random() * 0.22; g.addColorStop(0, `rgba(255,255,255,${a})`); g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r, 0, 7); ctx.fill();
      if (cx < r) { ctx.save(); ctx.translate(s, 0); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r, 0, 7); ctx.fill(); ctx.restore(); }
    }
    const tex = new THREE.CanvasTexture(cv); tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.needsUpdate = true; return tex;
  }

  /** A tiny side-on ship silhouette (hull + two sails) for the lone crosser on the far horizon. */
  private shipSilTex(): THREE.Texture {
    const w = 128, h = 64, cv = document.createElement('canvas'); cv.width = w; cv.height = h; const ctx = cv.getContext('2d')!;
    ctx.fillStyle = '#fff';
    ctx.beginPath(); ctx.moveTo(20, 44); ctx.lineTo(108, 44); ctx.lineTo(98, 56); ctx.lineTo(30, 56); ctx.closePath(); ctx.fill(); // hull
    ctx.fillRect(62, 8, 3, 38); ctx.fillRect(40, 14, 3, 30);                                                                     // masts
    ctx.beginPath(); ctx.moveTo(65, 12); ctx.lineTo(86, 42); ctx.lineTo(65, 42); ctx.fill();                                     // main sail
    ctx.beginPath(); ctx.moveTo(43, 16); ctx.lineTo(58, 42); ctx.lineTo(43, 42); ctx.fill();                                     // fore sail
    const tex = new THREE.CanvasTexture(cv); tex.needsUpdate = true; return tex;
  }

  /** A soft "M" gull glyph. */
  private gullTex(): THREE.Texture {
    const s = 64, cv = document.createElement('canvas'); cv.width = cv.height = s; const ctx = cv.getContext('2d')!;
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 6; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(8, 40); ctx.quadraticCurveTo(26, 20, 32, 34); ctx.quadraticCurveTo(38, 20, 56, 40); ctx.stroke();
    const tex = new THREE.CanvasTexture(cv); tex.needsUpdate = true; return tex;
  }

  /** A soft rounded island-silhouette hill (alpha), used by the instanced distant-island layer. */
  private islandSilTex(): THREE.Texture {
    const w = 256, h = 96, cv = document.createElement('canvas'); cv.width = w; cv.height = h; const ctx = cv.getContext('2d')!;
    ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.moveTo(0, h);
    for (let x = 0; x <= w; x += 8) { const t = x / w; const y = h - (Math.sin(t * Math.PI) * (0.6 + 0.3 * Math.sin(t * 9))) * h * 0.8; ctx.lineTo(x, y); }
    ctx.lineTo(w, h); ctx.closePath(); ctx.fill();
    const tex = new THREE.CanvasTexture(cv); tex.needsUpdate = true; return tex;
  }

  /* ---- vista quality tiers + per-frame drive ---- */

  /** Apply a graphics tier to the vista: (re)size the portal target, rebuild the ocean at the tier's grid
   *  resolution, and toggle the tiered decorative layers (islands/ship/gulls). Low disables the offscreen
   *  portal entirely (the glass falls back to a cheap animated gradient) — the single biggest mobile lever. */
  setVistaQuality(preset: GraphicsPreset): void {
    this._vistaQuality = preset;
    const scene = this._vistaScene; if (!scene) return;
    const scale = VISTA.rtScale[preset];
    // portal target
    if (scale <= 0) { this._vistaRT?.dispose(); this._vistaRT = null; }
    else {
      const w = Math.max(2, Math.round(window.innerWidth * scale)), h = Math.max(2, Math.round(window.innerHeight * scale));
      if (!this._vistaRT) { this._vistaRT = new THREE.WebGLRenderTarget(w, h, { depthBuffer: true }); this._vistaRT.texture.colorSpace = THREE.LinearSRGBColorSpace; }
      else this._vistaRT.setSize(w, h);
    }
    // ocean grid resolution
    if (this._vistaOceanMat) {
      const mesh = scene.children.find((o) => (o as THREE.Mesh).material === this._vistaOceanMat) as THREE.Mesh | undefined;
      if (mesh) { mesh.geometry.dispose(); mesh.geometry = this.makeOceanGeo(VISTA.oceanSubdiv[preset]); }
    }
    // distant islands (instanced) — rebuilt to the tier's density
    if (this._vistaIslands) { scene.remove(this._vistaIslands); (this._vistaIslands as THREE.InstancedMesh).geometry?.dispose(); ((this._vistaIslands as THREE.InstancedMesh).material as THREE.Material)?.dispose(); this._vistaIslands = null; }
    const nIsland = VISTA.distantIslandDensity[preset];
    if (nIsland > 0) this._vistaIslands = this.buildIslands(scene, nIsland);
    // ship + gulls visibility
    if (this._vistaShip) this._vistaShip.visible = VISTA.distantShipEnabled && preset !== 'low';
    const nGull = VISTA.gullCount[preset];
    this._vistaGulls.forEach((g, i) => { g.visible = i < nGull; });
  }

  /** Build the instanced distant-island silhouettes on the far horizon (world-fixed → they parallax against the
   *  camera-locked sky). One draw call for the whole archipelago. */
  private buildIslands(scene: THREE.Scene, n: number): THREE.InstancedMesh {
    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = new THREE.MeshBasicMaterial({ map: this.islandSilTex(), transparent: true, depthWrite: false, fog: false, toneMapped: false, color: 0x223540, opacity: 0.85 });
    const inst = new THREE.InstancedMesh(geo, mat, n); inst.frustumCulled = false; inst.renderOrder = -1;
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), pos = new THREE.Vector3();
    const cx = HOUSE.WIDTH / 2, far = HouseScene.VFAR_Z - 10;
    for (let i = 0; i < n; i++) {
      const spread = (i / Math.max(1, n - 1) - 0.5) * VISTA.oceanSpanX * 0.72;
      const w = 16 + (i * 7 % 15), hgt = w * 0.34;
      // sit on the far waterline: base ≈ sea level, so the silhouette pokes above the hazed horizon into the sky.
      pos.set(cx + spread + (i * 5 % 7 - 3), VISTA.seaLevel + hgt * 0.42, far - (i * 9 % 22));
      sc.set(w, hgt, 1); m.compose(pos, q, sc); inst.setMatrixAt(i, m);
    }
    inst.instanceMatrix.needsUpdate = true; scene.add(inst); return inst;
  }

  /** Advance the vista animation + push the current day/night palette into every vista uniform. Called from
   *  updateInterior each frame; allocation-free (reuses palette-colour objects + scratch vectors). */
  private updateVista(t: number, phase: number): void {
    this._vistaT = t;
    const pal = this.vistaPaletteAt(phase);
    // sun/moon direction: fixed azimuth toward the far horizon, elevation from the palette (dawn/sunset ride low).
    const az = 0.35, elev = pal.sunY; // az>0 → glitter slightly east; elev 0=horizon … 1=high
    this._vScratchSun.set(Math.sin(az) * (1 - elev), 0.12 + elev * 0.9, Math.cos(az)).normalize();

    if (this._vistaOceanMat) {
      const u = this._vistaOceanMat.uniforms;
      u.uTime!.value = t * VISTA.oceanWaveSpeed;
      (u.uSunDir!.value as THREE.Vector3).copy(this._vScratchSun); u.uSunI!.value = pal.sunI;
    }
    if (this._vistaSkyMat) {
      const u = this._vistaSkyMat.uniforms;
      u.uCloudT!.value = t * VISTA.cloudSpeed; u.uStar!.value = pal.star;
      (u.uSunDir!.value as THREE.Vector3).copy(this._vScratchSun);
    }
    if (this._vistaSun) {
      const sm = this._vistaSun.material as THREE.SpriteMaterial; sm.color.copy(pal.sun); sm.opacity = 0.35 + pal.sunI * 0.4;
      // place the glow far along the sun direction from the room centre (never billboard-clipped by distance)
      this._vistaSun.position.set(HOUSE.WIDTH / 2 + this._vScratchSun.x * VISTA.backdropRadius * 0.72, VISTA.backdropHorizonY + 4 + elev * 34, HOUSE.DEPTH + this._vScratchSun.z * VISTA.backdropRadius * 0.72);
    }
    if (this._vistaShip && this._vistaShip.visible) {
      const span = VISTA.oceanSpanX * 0.8; const x = ((t * VISTA.distantShipSpeed) % (span * 2.4)) - span * 0.6; // slow crossing, long gaps off-view
      this._vistaShip.position.set(HOUSE.WIDTH / 2 + x, VISTA.seaLevel + 1.4, HouseScene.VFAR_Z - 8); // sits on the far waterline
    }
    for (const g of this._vistaGulls) {
      if (!g.visible) continue; const s = g.userData.seed as number; const a = t * 0.12 + s;
      g.position.set(HOUSE.WIDTH / 2 + Math.sin(a) * 22 + s * 4, VISTA.seaLevel + 7 + Math.sin(a * 1.7) * 3, HouseScene.VFAR_Z - 30 - (s % 3) * 6); // gulls wheel in the low sky
    }
    // subtle external cool light into the interior, scaled by daylight (never overpowering the warm hearth).
    if (this._vistaLight) { const day = Math.max(0, Math.min(1, -Math.cos(phase * Math.PI * 2))); this._vistaLight.intensity = 0.06 + day * 0.30; this._vistaLight.color.copy(pal.skyHaze).lerp(new THREE.Color(0xffffff), 0.3); }
    if (this._glassMat) { this._glassMat.uniforms.uGlow!.value = 0.35 + this._flick * 0.4; }
  }

  /** Render the offscreen vista into the portal target using the LIVE camera, so the glass samples a true
   *  see-through view with real parallax. Called at the tail of updateFollowCamera (camera already placed),
   *  right before the base render(). No-op on Low tier / when the RT is off → the glass uses its gradient
   *  fallback (uPortal=0). Cheap: the vista scene is ~6 objects at a downscaled resolution. */
  private renderVistaRT(): void {
    const rt = this._vistaRT, scene = this._vistaScene, glass = this._glassMat;
    if (!scene || !glass) return;
    if (!rt || !this._winPanes || this._winPanes.length === 0 || (this._winPanes[0] && !this._winPanes[0].visible)) { glass.uniforms.uPortal!.value = 0; return; }
    // keep the RT sized to the (downscaled) screen so screen-UV sampling stays 1:1 with the pane's projection.
    const w = Math.max(2, Math.round(window.innerWidth * VISTA.rtScale[this._vistaQuality]));
    const h = Math.max(2, Math.round(window.innerHeight * VISTA.rtScale[this._vistaQuality]));
    if (rt.width !== w || rt.height !== h) rt.setSize(w, h);
    // the sky backdrop + camera uniforms track the live camera (near sea is world-fixed → real parallax).
    const cam = this.camera; this._vScratchCam.copy(cam.position);
    if (this._vistaSky) this._vistaSky.position.set(cam.position.x, 0, cam.position.z);
    if (this._vistaOceanMat) (this._vistaOceanMat.uniforms.uCam!.value as THREE.Vector3).copy(this._vScratchCam);
    glass.uniforms.uPortal!.value = 1; (glass.uniforms.uCam!.value as THREE.Vector3).copy(this._vScratchCam);
    glass.uniforms.uRT!.value = rt.texture;
    const prevTarget = this.webgl.getRenderTarget();
    this.webgl.setRenderTarget(rt); this.webgl.render(scene, cam); this.webgl.setRenderTarget(prevTarget);
  }

  /** Resolve the vista day/night palette at `phase` into the reused scratch (allocation-free). `seed` forces a
   *  full copy even on the first call. Colours are stylized sRGB (non-PBR) — lerped from VISTA_KEYS. */
  private vistaPaletteAt(phase: number, _seed = false): typeof this._vistaPal {
    const p = ((phase % 1) + 1) % 1; let a = VISTA_KEYS[0]!, b = VISTA_KEYS[VISTA_KEYS.length - 1]!;
    for (let i = 0; i < VISTA_KEYS.length - 1; i++) { if (p >= VISTA_KEYS[i]!.p && p <= VISTA_KEYS[i + 1]!.p) { a = VISTA_KEYS[i]!; b = VISTA_KEYS[i + 1]!; break; } }
    const tt = (p - a.p) / Math.max(1e-6, b.p - a.p), o = this._vistaPal;
    o.skyTop.copy(a.skyTop).lerp(b.skyTop, tt); o.skyHaze.copy(a.skyHaze).lerp(b.skyHaze, tt);
    o.seaDeep.copy(a.seaDeep).lerp(b.seaDeep, tt); o.seaShallow.copy(a.seaShallow).lerp(b.seaShallow, tt);
    o.sun.copy(a.sun).lerp(b.sun, tt); o.sunI = a.sunI + (b.sunI - a.sunI) * tt; o.sunY = a.sunY + (b.sunY - a.sunY) * tt; o.star = a.star + (b.star - a.star) * tt;
    return o;
  }

  /** Place the iron-stove GLB on the shell hearth footprint (hiding the procedural mantel) + an additive
   *  ember glow at its mouth that pulses with the flicker. */
  private placeHearthStove(): void {
    if (!this.stoveBake) return;
    this.clearGroup(this.hearthRoot);
    const b = this.stoveBake, hx = HOUSE_SHELL.hearth.x, D = HOUSE.DEPTH;
    const mesh = new THREE.Mesh(b.geo, b.mat); mesh.scale.setScalar(b.scale ?? 1);
    mesh.position.set(hx, b.groundY ?? 0, D - 1.0); mesh.rotation.y = Math.PI; // fire faces south, into the room
    this.hearthRoot.add(mesh);
    this._ensureLiveRefs();
    for (const n of this._hearthProc ?? []) n.visible = false; // the GLB replaces the procedural mantel/embers
    this._glbEmberMat = new THREE.MeshBasicMaterial({ color: 0xff6a14, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
    const ember = new THREE.Mesh(new THREE.PlaneGeometry(0.8, 0.5), this._glbEmberMat); ember.position.set(hx, 0.55, D - 1.55); this.hearthRoot.add(ember);
  }

  /** A handful of warm additive light shafts from the stern windows + dozens of slow dust motes inside them.
   *  Intensity is driven by the time of day in updateInterior (strong noon → moonlit at night). */
  private buildGodRaysAndDust(): void {
    if (this._godRays) return;
    const W = HOUSE.WIDTH, D = HOUSE.DEPTH;
    this._godRayMat = new THREE.MeshBasicMaterial({ color: 0xffe6b0, map: this.softDotTex(), transparent: true, opacity: 0.13, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const slabs: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 4; i++) {
      const sx = (W / 4) * (i + 0.5);
      const g = new THREE.PlaneGeometry(1.7, 4.6);        // a tall shaft, tilted to rake down into the room (soft-edged via the dot map)
      g.rotateX(-Math.PI / 2 + 0.62); g.rotateY((i - 1.5) * 0.06);
      g.translate(sx, 1.7, D - 2.2);
      slabs.push(g);
    }
    const merged = mergeGeometries(slabs, false) ?? slabs[0]!;
    this._godRays = new THREE.Mesh(merged, this._godRayMat); this._godRays.frustumCulled = false; this._godRays.renderOrder = 2;
    this.scene.add(this._godRays);
    // dust motes drifting within the shaft volume
    const N = 54; const pos = new Float32Array(N * 3); this._dustVel = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      pos[i * 3] = 1 + Math.random() * (W - 2); pos[i * 3 + 1] = 0.5 + Math.random() * 2.4; pos[i * 3 + 2] = D - 0.6 - Math.random() * 4.5;
      this._dustVel[i * 3] = (Math.random() - 0.5) * 0.06; this._dustVel[i * 3 + 1] = 0.02 + Math.random() * 0.05; this._dustVel[i * 3 + 2] = (Math.random() - 0.5) * 0.05;
    }
    const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const mat = new THREE.PointsMaterial({ size: 0.09, map: this.softDotTex(), transparent: true, opacity: 0.5, depthWrite: false, blending: THREE.AdditiveBlending, color: 0xfff1cf, fog: false });
    this._dust = new THREE.Points(geo, mat); this._dust.frustumCulled = false; this.scene.add(this._dust);
  }

  /** Collect the shell's live-driven nodes (hearth light, procedural embers/mantel, window panes) once — the
   *  shell is built during super() so HouseScene can only grab them lazily on the first frame/interior load. */
  private _ensureLiveRefs(): void {
    if (this._hearthProc) return;
    this._hearthProc = []; this._winPanes = [];
    this.staticRoot.traverse((o) => {
      if (o.userData.hearthLight) this._hearthLight = o as THREE.PointLight;
      if (o.userData.hearthEmber) { const m = (o as THREE.Mesh).material; if (m && !Array.isArray(m)) this._emberMat = m as THREE.MeshStandardMaterial; }
      if (o.userData.hearthProc) this._hearthProc!.push(o);
      if (o.userData.window !== undefined) this._winPanes!.push(o as THREE.Mesh);
      if ((o as THREE.HemisphereLight).isHemisphereLight && !this._houseHemi) { this._houseHemi = o as THREE.HemisphereLight; this._houseHemiBase.copy(this._houseHemi.color); }
    });
  }

  /** Per-frame interior life, run before the inherited avatar/camera sync. When seated, the local avatar's
   *  transform is snapped to the seat (super still renders + frames it); walking away releases the pose. */
  override sync(
    local: { x: number; y: number; heading: number; moving: boolean; cls: number; skin: number } | null,
    yourId: number,
    remotes: { id: number; x: number; y: number; heading: number; moving: boolean; cls: number; skin: number; name: string; badge?: number }[],
    t: number, dt: number,
  ): void {
    this._yourId = yourId;
    if (this._editorSit) {
      // Seat-config preview: pin the ghost avatar into the configured seat (bypasses nearestSeat + auto-stand).
      this._sitAnchor = this._editorSit; this._sitFrom = { x: this._editorSit.x, y: this._editorSit.y }; this._sitting = true;
      if (yourId >= 0) this.setSit(yourId, true, this._editorSit); // v43 setSit takes the full seat transform
    } else if (this._sitting && local && this._sitFrom) {
      const dx = local.x - this._sitFrom.x, dy = local.y - this._sitFrom.y;
      if (dx * dx + dy * dy > 0.6 * 0.6) this.standUp(); // the player walked away from where they sat down
    }
    const shown = (this._sitting && local && this._sitAnchor)
      ? { x: this._sitAnchor.x, y: this._sitAnchor.y, heading: this._sitAnchor.heading, moving: false, cls: local.cls, skin: local.skin }
      : local;
    this.updateInterior(t, dt);
    super.sync(shown, yourId, remotes, t, dt);
  }

  /** Drive the flicker, dust drift, god-ray + seascape time-of-day, candle flames and cloth sway. */
  private updateInterior(t: number, dt: number): void {
    SWAY_TIME.value = t;
    this._ensureLiveRefs();
    // organic flicker: a few slow sines (2–4 Hz band), smoothed so it dances warm and never strobes.
    const raw = 0.5 + 0.3 * Math.sin(t * 2.7) + 0.17 * Math.sin(t * 4.3 + 2.0) + 0.08 * Math.sin(t * 9.1 + 1.1);
    this._flick += (Math.max(0, Math.min(1, raw)) - this._flick) * Math.min(1, dt * 10);
    const fl = this._flick;
    if (this._hearthLight) {
      this._hearthLight.intensity = 1.25 + fl * 0.95;
      this._hearthLight.position.x = HOUSE_SHELL.hearth.x + Math.sin(t * 5.1) * 0.04;
      this._hearthLight.position.y = 1.2 + Math.sin(t * 3.7 + 1.0) * 0.05;
    }
    if (this._emberMat && (this._hearthProc?.[0]?.visible ?? false)) this._emberMat.emissiveIntensity = 1.0 + fl * 1.3;
    if (this._glbEmberMat) this._glbEmberMat.opacity = 0.5 + fl * 0.45;
    if (this._candleMat) this._candleMat.emissiveIntensity = 1.15 + fl * 0.75;

    // time of day → god rays + the living ExteriorVista (sea/sky/sun/distant life, all day-night driven)
    const phase = this.interiorPhase();
    const daylight = Math.max(0, Math.min(1, -Math.cos(phase * Math.PI * 2))); // 1 at noon, 0 at midnight
    // harmonize with the exterior city cycle (same clock, same nightFactor curve) without duplicating it:
    // a subtle cool cast on the shell fill light at night — the hearth/candles keep the room warm below.
    if (this._houseHemi) this._houseHemi.color.copy(this._houseHemiBase).lerp(HOUSE_NIGHT_TINT, cityPaletteAt(phase, this._hcPal).nightFactor * 0.16);
    if (this._godRayMat) this._godRayMat.opacity = 0.05 + daylight * 0.26 + fl * 0.02;
    if (this._dust) { (this._dust.material as THREE.PointsMaterial).opacity = 0.28 + daylight * 0.42; this.driftDust(dt); }
    this.updateVista(t, phase); // advance the ocean/sky/gulls + push the day-night palette into every vista uniform
  }

  /** Advance the dust motes slowly, wrapping them within the shaft volume. */
  private driftDust(dt: number): void {
    const p = this._dust!.geometry.attributes.position as THREE.BufferAttribute;
    const v = this._dustVel!, W = HOUSE.WIDTH, D = HOUSE.DEPTH, arr = p.array as Float32Array;
    for (let i = 0; i < arr.length; i += 3) {
      let x = arr[i]! + v[i]! * dt, y = arr[i + 1]! + v[i + 1]! * dt, z = arr[i + 2]! + v[i + 2]! * dt;
      if (y > 3.0) { y = 0.4; x = 1 + Math.random() * (W - 2); z = D - 0.6 - Math.random() * 4.5; }
      if (x < 0.5 || x > W - 0.5) v[i] = -v[i]!;
      arr[i] = x; arr[i + 1] = y; arr[i + 2] = z;
    }
    p.needsUpdate = true;
  }

  /** The interior time-of-day phase — the same wall-clock cycle the sea sky uses, unless an E2E test pins it. */
  private interiorPhase(): number { return this._phaseOverride != null ? this._phaseOverride : dayNightPhase(Date.now()); }
  /** E2E hook: pin the interior time-of-day (render-only), or pass null to resume the live cycle. Overrides the
   *  base PortScene hook so __cc.setCityPhase drives the cabin's OWN time-of-day system (windows/godrays). */
  override setPhaseOverride(p: number | null): void { this._phaseOverride = p; }

  /** (Re)build ONE instanced flame + halo across every candelabrum + wall-lantern flame in the house. */
  private buildCandleFlames(): void {
    const spots: { x: number; y: number; z: number }[] = [];
    for (const p of this.state?.placed ?? []) {
      const c = this.cellCentre(p.kind, p.cell, p.rot);
      const pf = clampHouseScale(p.scale) / 100; // v42: flames track the piece's decorate scale
      const yaw = -p.rot * Math.PI / 2, cs = Math.cos(yaw), sn = Math.sin(yaw);
      const rot = (lx: number, lz: number): { x: number; z: number } => ({ x: c.x + lx * pf * cs + lz * pf * sn, z: c.y - lx * pf * sn + lz * pf * cs });
      if (p.kind === FurnitureKind.CANDELABRA) {
        for (const lx of [-0.34, -0.17, 0, 0.17, 0.34]) { const r = rot(lx, 0); spots.push({ x: r.x, y: 1.2 * pf, z: r.z }); }
      } else if (p.kind === FurnitureKind.WALL_LAMP) {
        const r = rot(0, -0.08); spots.push({ x: r.x, y: 1.42 * pf, z: r.z });
      }
    }
    if (!spots.length) { this._candleMat = null; return; }
    this._candleMat = new THREE.MeshStandardMaterial({ color: 0xffb347, emissive: 0xff9a1a, emissiveIntensity: 1.2, roughness: 0.5 });
    const flameGeo = new THREE.SphereGeometry(0.05, 6, 6); flameGeo.scale(1, 1.7, 1);
    // small, faint halos: five candles on one candelabrum sit close, so a bright halo would bloom into one sun.
    const haloMat = new THREE.MeshBasicMaterial({ color: 0xffcf7a, transparent: true, opacity: 0.14, blending: THREE.AdditiveBlending, depthWrite: false, fog: false });
    const haloGeo = new THREE.SphereGeometry(0.1, 8, 8);
    const flame = new THREE.InstancedMesh(flameGeo, this._candleMat, spots.length); flame.frustumCulled = false;
    const halo = new THREE.InstancedMesh(haloGeo, haloMat, spots.length); halo.frustumCulled = false;
    const m4 = new THREE.Matrix4();
    spots.forEach((s, i) => { m4.makeTranslation(s.x, s.y, s.z); flame.setMatrixAt(i, m4); halo.setMatrixAt(i, m4); });
    flame.instanceMatrix.needsUpdate = true; halo.instanceMatrix.needsUpdate = true;
    this._candleFlame = flame; this._candleHalo = halo;
    this.furnitureRoot.add(halo); this.furnitureRoot.add(flame);
  }

  /* ------------------------------- Sit (contextual, local visual only) ------------------------------- */

  /** The seat transform of the nearest sittable piece (chair/bench/bed) within reach of (x,y), or null. The
   *  geometry is the pure `nearestSeat` in @piratas/sim (unit-tested there) so the picker can never drift from
   *  the render. The avatar is seated facing the room centre — reads natural at a table, a bench or a bed edge. */
  sittableAt(x: number, y: number): SeatSpot | null {
    return nearestSeat(this.state?.placed ?? [], x, y);
  }

  /** Act on a Sit press at the player's current position:
   *  - already seated → stand up (`'stood'`);
   *  - a seat within reach → sit on it, snapping to its transform (`'sat'`);
   *  - no seat in reach → `'noseat'` so the caller can toast "find a seat first".
   *  v43: sitting/standing fires onSitWire (inherited) so the server re-broadcasts the pose to every visitor. */
  toggleSit(x: number, y: number): 'sat' | 'stood' | 'noseat' {
    if (this._sitting) { this.standUp(); return 'stood'; }
    const seat = this.sittableAt(x, y);
    if (!seat) return 'noseat';
    this._sitAnchor = seat; this._sitFrom = { x, y }; this._sitting = true;
    if (this._yourId >= 0) this.setSit(this._yourId, true, seat);
    this.onSitWire?.(true);
    return 'sat';
  }

  private standUp(): void {
    if (!this._sitting) return;
    this._sitting = false; this._sitAnchor = null; this._sitFrom = null;
    if (this._yourId >= 0) this.setSit(this._yourId, false);
    this.onSitWire?.(false);
  }

  isSitting(): boolean { return this._sitting; }

  override reset(): void { this.standUp(); super.reset(); }

  /** (Re)build the furniture + trophy-wall layers from a fresh S_HOUSE_STATE. ONE InstancedMesh per kind. */
  setHouseState(state: HouseStateMsg): void {
    this.state = state;
    if (state.placed.some((p) => p.kind === FurnitureKind.BED)) void this.preloadBed();
    void this.preloadInterior();
    this.rebuildFurniture();
    this.rebuildSlots();
  }

  private rebuildFurniture(): void {
    this.clearGroup(this.furnitureRoot);
    const placed = this.state?.placed ?? [];
    const byKind = new Map<number, { cell: number; rot: number; scale?: number; yaw?: number; fx?: number; fy?: number }[]>();
    for (const p of placed) { let a = byKind.get(p.kind); if (!a) byKind.set(p.kind, a = []); a.push(p); }
    let lights = 0;
    for (const [kind, places] of byKind) {
      const bake = this.furnTemplate(kind); if (!bake) continue;
      const baseScale = bake.scale ?? 1, groundY = bake.groundY ?? 0;
      const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), pos = new THREE.Vector3(), sc = new THREE.Vector3();
      const inst = new THREE.InstancedMesh(bake.geo, bake.mat, places.length); inst.frustumCulled = false;
      inst.userData.furnKind = kind; // editor hit-test: pickPlacement maps (furnKind, instanceId) → the placed piece
      let glow: THREE.InstancedMesh | null = null;
      if (bake.glowGeo && bake.glowMat) { glow = new THREE.InstancedMesh(bake.glowGeo, bake.glowMat, places.length); glow.frustumCulled = false; }
      places.forEach((p, i) => {
        const c = this.cellCentre(kind, p.cell, p.rot);
        // v42: per-piece decorate scale (percent → factor). It multiplies the bake's fit-scale and lifts the
        // groundY the same amount so a resized piece still rests on the floor. v43: the fine VISUAL transform —
        // `furnitureYaw` (256-step rotation; absent → the legacy cardinal -rot*π/2) + `furnitureOffset` (sub-cell
        // nudge in metres; absent → 0). Footprint/collision stay the coarse cell/rot, unchanged.
        const off = furnitureOffset(p.fx, p.fy);
        const pf = clampHouseScale(p.scale) / 100;
        const s = baseScale * pf;
        q.setFromAxisAngle(UP, furnitureYaw(p.rot, p.yaw));
        sc.set(s, s, s);
        pos.set(c.x + off.ox, groundY * pf, c.y + off.oy); m4.compose(pos, q, sc);
        inst.setMatrixAt(i, m4); glow?.setMatrixAt(i, m4);
        if (bake.light && lights < 10) { const L = new THREE.PointLight(bake.light.color, bake.light.intensity, bake.light.dist, 1.6); L.position.set(c.x + off.ox, bake.light.y * pf, c.y + off.oy); this.furnitureRoot.add(L); lights++; }
      });
      inst.instanceMatrix.needsUpdate = true; this.furnitureRoot.add(inst);
      if (glow) { glow.instanceMatrix.needsUpdate = true; this.furnitureRoot.add(glow); }
    }
    this.buildCandleFlames(); // one instanced flame+halo across all candelabra + wall lanterns
  }

  /** House-local centre of a placed piece: origin cell (gx,gy) + half its (rotated) footprint. */
  private cellCentre(kind: number, cell: number, rot: number): { x: number; y: number } {
    const gx = cell % HOUSE.WIDTH, gy = Math.floor(cell / HOUSE.WIDTH);
    const fp = furnitureFootprint(kind, rot);
    return { x: gx + fp.w / 2, y: gy + fp.d / 2 };
  }

  /** Build (once, cached) the baked template for a kind — the hero BED/STOVE/CANDELABRA use their GLB when
   *  loaded; the CURTAIN gets a cloth-sway material so its hem billows. */
  private furnTemplate(kind: number): FurnBake | null {
    if (kind === FurnitureKind.BED && this.bedBake) return this.bedBake;
    if (kind === FurnitureKind.IRON_STOVE && this.stoveBake) return this.stoveBake;
    if (kind === FurnitureKind.CANDELABRA && this.candelabrumBake) return this.candelabrumBake;
    if (kind === FurnitureKind.CARD_TABLE && this.cardTableBake) return this.cardTableBake;
    if (kind === FurnitureKind.GLOBE && this.globeBake) return this.globeBake;
    if (kind === FurnitureKind.AQUARIUM && this.aquariumBake) return this.aquariumBake;
    if (kind === FurnitureKind.CHART_TABLE && this.chartTableBake) return this.chartTableBake;
    if (kind === FurnitureKind.BOOKSHELF && this.bookshelfBake) return this.bookshelfBake;
    if (kind === FurnitureKind.STRANGE_LAMP && this.strangeLampBake) return this.strangeLampBake;
    if (kind === FurnitureKind.DECO_BARREL && this.rumBarrelBake) return this.rumBarrelBake;
    if (kind === FurnitureKind.SPYGLASS && this.spyglassBake) return this.spyglassBake;
    const cached = this.furnBakes.get(kind); if (cached) return cached;
    const parts = furnitureParts(kind);
    const geo = mergeParts(parts.solid, true); if (!geo) return null;
    // the curtain sways from its rod (WALL_H-0.4); everything else is a plain vertex-coloured solid.
    const mat = kind === FurnitureKind.CURTAIN
      ? this.clothSwayMaterial(WALL_H - 0.4, 0.11, true)
      : new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.08 });
    const bake: FurnBake = { geo, mat };
    if (parts.glow && parts.glow.length) {
      const gg = mergeParts(parts.glow, false);
      if (gg) { bake.glowGeo = gg; bake.glowMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, emissive: parts.glowColor ?? 0xffb347, emissiveIntensity: 1.2, roughness: 0.5 }); }
    }
    if (parts.light) bake.light = parts.light;
    this.furnBakes.set(kind, bake);
    return bake;
  }

  /** Load + bake the hero Captain's Berth GLB, then rebuild furniture so it swaps in. Procedural until then. */
  private async preloadBed(): Promise<void> {
    if (this.bedLoading || this.bedBake) return;
    this.bedLoading = true;
    try {
      const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/libs/meshopt_decoder.module.js'),
      ]);
      const loader = new GLTFLoader() as unknown as { loadAsync(u: string): Promise<{ scene: THREE.Object3D }>; setMeshoptDecoder(d: unknown): void };
      loader.setMeshoptDecoder(MeshoptDecoder);
      const gltf = await loader.loadAsync(BED_URL);
      const obj = gltf.scene; obj.position.set(0, 0, 0); obj.rotation.set(0, 0, 0); obj.scale.set(1, 1, 1); obj.updateMatrixWorld(true);
      const geos: THREE.BufferGeometry[] = []; let mat: THREE.Material | null = null;
      obj.traverse((o) => {
        const m = o as THREE.Mesh; if (!m.isMesh || !m.geometry) return;
        const g = m.geometry.clone();
        for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'normal' && name !== 'uv') g.deleteAttribute(name);
        g.morphAttributes = {}; g.morphTargetsRelative = false; // drop morph data so a multi-mesh GLB (e.g. a sail-morph hull) merges (see preloadShipModel)
        if (!g.attributes.normal) g.computeVertexNormals();
        if (!g.attributes.uv) { const n = g.attributes.position!.count; g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2)); }
        g.applyMatrix4(m.matrixWorld); geos.push(g); if (!mat) mat = Array.isArray(m.material) ? m.material[0]! : m.material;
      });
      if (!geos.length || !mat) throw new Error('empty bed glb');
      this.normalizeFurnMat(mat); // skull_bed is a leva-3 export → same fullbright-white risk as the other heroes
      const merged = geos.length === 1 ? geos[0]! : (mergeGeometries(geos, false) ?? geos[0]!);
      const bbox = new THREE.Box3().setFromBufferAttribute(merged.attributes.position as THREE.BufferAttribute);
      const size = bbox.getSize(new THREE.Vector3());
      const foot = furnitureById(FurnitureKind.BED)!.foot; // 3×2 cells
      const scale = Math.min(foot.w / (size.x || 1), foot.d / (size.z || 1));
      this.bedBake = { geo: merged, mat, isGlb: true, scale, groundY: -bbox.min.y * scale };
      this.rebuildFurniture();
    } catch (err) { console.warn('[house] bed GLB load failed — procedural berth', err); }
  }

  private rebuildSlots(): void {
    this.clearGroup(this.slotRoot);
    // v44: the owner (or the editor template) hid the hard-coded trophy wall — leave the slot root empty. The
    // flag rides on S_HOUSE_STATE, so visitors respect it too, and the editor's buildState() drives the preview.
    if (this.state?.hideTrophyWall) return;
    const fills = new Map<number, { n: number; text: string }>();
    for (const s of this.state?.slots ?? []) fills.set(s.kind, { n: s.n, text: s.text });
    for (const slot of HOUSE_SHELL.slots) {
      const fill = fills.get(slot.kind);
      const g = this.buildSlot(slot.kind, fill?.n ?? 0, fill?.text ?? '');
      g.position.set(slot.x, 0, slot.y); g.rotation.y = -slot.heading;
      this.slotRoot.add(g);
    }
  }

  /** Build one merged mesh for a category of slot parts, or null if the category is empty. A fresh material per
   *  call so clearGroup can dispose it safely (slot geos/mats are one-off, never in the cached-geo set). */
  private slotMesh(parts: Part[], mat: THREE.Material): THREE.Mesh | null {
    const geo = mergeParts(parts, true);
    return geo ? new THREE.Mesh(geo, mat) : null;
  }

  private buildSlot(kind: number, n: number, text: string): THREE.Group {
    const empty = n <= 0;
    // A filled slot's parts are sorted into THREE materials so each piece reads right instead of merging into one
    // flat clay blob (the old "feijão" look — a single roughness-0.8 / metalness-0.15 mesh made the trophies and
    // codex spheres read as dark beans):
    //  · base — matte wood/stone/parchment mounts + the dusty cover on an empty slot.
    //  · gold — trophies / cups / gilt frames: a warm metallic sheen with a low emissive FLOOR so they never fall
    //           to black in the gloom (there's no scene env-map, so a plain high-metalness gold reads near-black).
    //  · glow — codex relics: self-lit arcane crystals (a recognisable artefact, not a bare green ball).
    const base: Part[] = [];
    const gold: Part[] = [];
    const glow: Part[] = [];
    const glowColor = 0x35e6b4;
    let label = '';
    // Onda 8 polish: an EMPTY slot no longer shows a bare mount (which read as a brown cardboard box). It keeps
    // its wall shelf / frame / pedestal but drapes a dusty dust-sheet over it + names the feat on the plaque, so
    // it reads "earn it to unveil". The mount + a `cover` (where to drape) is set per kind; the ship slot is
    // never empty (every captain has a flagship) and swaps in the real hull GLB.
    let cover: [number, number, number, number, number, number] | null = null; // x,y,z,rx,ry,rz for dustSheet
    switch (kind) {
      case TrophySlotKind.TROPHY_SHELF: {
        base.push(box(1.0, 0.08, 0.3, 0.15, 1.2, 0, C.wood)); // a warm-lit shelf board (mid wood, not near-black woodDark → reads against the wall)
        if (empty) { cover = [0.15, 1.34, 0, 0.5, 0.16, 0.2]; label = 'Trophy Shelf'; }
        else {
          const cnt = Math.min(5, n);
          for (let i = 0; i < cnt; i++) {          // a mini gold chalice: conical foot · thin stem · flared bowl
            const x = 0.15 + (i - (cnt - 1) / 2) * 0.24; // spacing > the flared-bowl width so the cups read as a ROW, never one merged gold blob
            gold.push(cyl(0.085, 0.11, 0.045, 10, x, 1.265, 0, C.gold));
            gold.push(cyl(0.026, 0.026, 0.12, 8, x, 1.345, 0, C.gold));
            gold.push(cyl(0.1, 0.05, 0.15, 12, x, 1.48, 0, C.gold));
          }
          label = `Trophies ×${n}`;
        }
        break;
      }
      case TrophySlotKind.CODEX_RELIC: {
        base.push(box(1.0, 0.08, 0.3, 0.15, 1.2, 0, C.wood)); // warm-lit shelf board (see TROPHY_SHELF — woodDark read as black against the dim wall)
        if (empty) { cover = [0.15, 1.34, 0, 0.5, 0.16, 0.2]; label = 'Codex Relics'; }
        else {
          const cnt = Math.min(4, n);
          for (let i = 0; i < cnt; i++) {          // a glowing arcane crystal standing on a dark stone plinth
            const x = 0.15 + (i - (cnt - 1) / 2) * 0.24;
            base.push(cyl(0.085, 0.1, 0.05, 8, x, 1.265, 0, C.iron));
            const gem = new THREE.OctahedronGeometry(0.1, 0); gem.scale(0.85, 1.5, 0.85); gem.translate(x, 1.47, 0);
            glow.push({ geo: gem, color: 0x4defc0 });
          }
          label = `${n} Codex set${n === 1 ? '' : 's'}`;
        }
        break;
      }
      case TrophySlotKind.WANTED_POSTER:
        base.push(box(1.0, 1.2, 0.04, 0, 1.5, 0.02, C.woodDark));
        if (empty) { cover = [0, 1.5, 0.16, 0.56, 0.62, 0.1]; label = 'Wanted Poster'; }
        else { base.push(box(0.9, 1.1, 0.06, 0, 1.5, 0.06, C.parch)); label = `WANTED · ${n} 🪙`; }
        break;
      case TrophySlotKind.CLANWAR_CUP: {
        // A stepped MARBLE pedestal (the old 0x5a5148 stone was so dark it crushed to a pure-black box against the
        // dim east wall — same void bug as the trophy shelves): lighter warm marble + a gold base-plate + a gilt
        // trim ring so it reads as a plinth, lit further by the slot spotlight.
        base.push(box(0.72, 0.08, 0.72, 0, 0.04, 0, 0xb7ac97)); // wide grounded foot
        base.push(box(0.6, 0.34, 0.6, 0, 0.25, 0, 0xa89e8a));   // marble column
        base.push(box(0.7, 0.07, 0.7, 0, 0.45, 0, 0xc2b8a2));   // beveled cap
        gold.push(box(0.64, 0.045, 0.64, 0, 0.5, 0, C.gold));    // engraved gold base-plate the cup stands on
        if (empty) { cover = [0, 0.72, 0, 0.44, 0.34, 0.44]; label = 'Clan War Cup'; }
        else {                                     // a rounder, taller two-handled champion's chalice — gleaming gold
          gold.push(cyl(0.19, 0.23, 0.045, 18, 0, 0.545, 0, C.gold)); // base disc
          gold.push(cyl(0.07, 0.18, 0.1, 18, 0, 0.61, 0, C.gold));    // flared foot
          gold.push(cyl(0.05, 0.06, 0.16, 12, 0, 0.72, 0, C.gold));   // stem
          gold.push(sph(0.062, 0, 0.82, 0, C.gold));                  // knop bead on the stem
          gold.push(cyl(0.225, 0.095, 0.28, 22, 0, 1.0, 0, C.gold));  // deep flared bowl (many segments → round, not a funnel)
          { const lip = new THREE.TorusGeometry(0.225, 0.022, 8, 24); lip.rotateX(Math.PI / 2); lip.translate(0, 1.14, 0); gold.push({ geo: lip, color: C.gold }); } // rounded rim
          for (const sx of [-1, 1]) {                // bold looping ear handles, clearly attached from lip to bowl
            const h = new THREE.TorusGeometry(0.115, 0.026, 8, 18, Math.PI * 1.25); h.rotateY(Math.PI / 2); h.rotateZ(sx * Math.PI * 0.55); h.translate(sx * 0.215, 1.0, 0); gold.push({ geo: h, color: C.gold });
          }
          label = (text || 'Clan War');
        }
        break;
      }
      case TrophySlotKind.SHIP_MODEL:
        base.push(box(1.4, 0.12, 1.4, 0, 0.95, 0, C.wood)); // the map table (the mini hull is added below)
        label = 'Flagship';
        break;
      case TrophySlotKind.SEASON_PORTRAIT:
        gold.push(box(1.0, 1.2, 0.05, 0, 1.55, 0.04, C.gold));                        // gilt frame
        base.push(box(0.82, 1.0, 0.03, 0, 1.55, 0.02, empty ? 0x3f362e : 0x5a708a));  // canvas
        if (empty) { cover = [0, 1.55, 0.14, 0.6, 0.62, 0.1]; label = 'Season Portrait'; }
        else label = `Season · Lv ${n}`;
        break;
      default:
        base.push(box(0.4, 0.4, 0.4, 0, 0.6, 0, C.wood));
    }
    if (cover) base.push(dustSheet(...cover));
    const g = new THREE.Group();
    const baseMesh = this.slotMesh(base, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.08 }));
    if (baseMesh) g.add(baseMesh);
    const goldMesh = this.slotMesh(gold, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.32, metalness: 0.4, emissive: 0x8a5f16, emissiveIntensity: 0.85 }));
    if (goldMesh) g.add(goldMesh);
    const glowMesh = this.slotMesh(glow, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.32, metalness: 0.1, emissive: glowColor, emissiveIntensity: 0.85 }));
    if (glowMesh) g.add(glowMesh);
    if (kind === TrophySlotKind.SHIP_MODEL) this.addShipModel(g, Math.max(0, n - 1)); // n = shipClass + 1
    if (!empty) g.add(this.trophySpotlight());  // museography: a dedicated fake spotlight for a filled slot
    if (label) { const spr = this.makeLabel(label); spr.position.set(0, kind === TrophySlotKind.CLANWAR_CUP || kind === TrophySlotKind.SHIP_MODEL ? 1.9 : 2.3, 0.1); g.add(spr); }
    return g;
  }

  /** Stand the captain's flagship on the map-table trophy slot: the real hull GLB (baked to ~0.5 m, bow +X),
   *  or — until it streams in / if it fails — a small stylised procedural boat (never the old fat blob). */
  private addShipModel(g: THREE.Group, cls: number): void {
    const bake = this.shipBakes.get(cls);
    const tableTop = 1.01; // map table = box centred y=0.95, 0.12 thick → top ≈ 1.01
    if (bake) {
      const mesh = new THREE.Mesh(bake.geo, bake.mat);
      mesh.scale.setScalar(bake.scale ?? 1);
      mesh.position.set(0, tableTop + (bake.groundY ?? 0), 0);
      mesh.rotation.y = Math.PI; // hull GLBs bow toward -X; face it +X like the sea renderer
      g.add(mesh);
      return;
    }
    // placeholder while the GLB loads (or a permanent fallback): a little planked hull + mast + sail
    const hull = new THREE.SphereGeometry(0.3, 14, 9); hull.scale(1.7, 0.42, 0.62); hull.translate(0, tableTop + 0.16, 0);
    const bow = new THREE.ConeGeometry(0.12, 0.34, 10); bow.rotateZ(-Math.PI / 2); bow.scale(1, 0.42, 0.62); bow.translate(0.5, tableTop + 0.16, 0);
    const ph = mergeParts([{ geo: hull, color: C.woodDark }, { geo: bow, color: C.woodDark },
      cyl(0.02, 0.02, 0.62, 6, 0, tableTop + 0.5, 0, C.wood),
      box(0.02, 0.42, 0.3, 0.02, tableTop + 0.55, 0, C.mattress)], true);
    if (ph) g.add(new THREE.Mesh(ph, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0.1 })));
    void this.preloadShipModel(cls);
  }

  /** Lazily load + bake the owner's ship-class hull GLB to a ~0.5 m mini, then rebuild the trophy wall so the
   *  Flagship slot swaps the placeholder for the real hull. Cached per class; degrades to the placeholder. */
  private async preloadShipModel(cls: number): Promise<void> {
    if (this.shipBakes.has(cls) || this.shipLoading.has(cls)) return;
    const url = SHIP_MODEL_URLS[cls]; if (!url) return;
    this.shipLoading.add(cls);
    try {
      const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
        import('three/addons/loaders/GLTFLoader.js'),
        import('three/addons/libs/meshopt_decoder.module.js'),
      ]);
      const loader = new GLTFLoader() as unknown as { loadAsync(u: string): Promise<{ scene: THREE.Object3D }>; setMeshoptDecoder(d: unknown): void };
      loader.setMeshoptDecoder(MeshoptDecoder);
      const gltf = await loader.loadAsync(url);
      const obj = gltf.scene; obj.position.set(0, 0, 0); obj.rotation.set(0, 0, 0); obj.scale.set(1, 1, 1); obj.updateMatrixWorld(true);
      const geos: THREE.BufferGeometry[] = []; let mat: THREE.Material | null = null;
      obj.traverse((o) => {
        const m = o as THREE.Mesh; if (!m.isMesh || !m.geometry) return;
        const gg = m.geometry.clone();
        for (const name of Object.keys(gg.attributes)) if (name !== 'position' && name !== 'normal' && name !== 'uv') gg.deleteAttribute(name);
        // The ship hull GLBs are 3 meshes where ONLY the sail carries a wave morph target — inconsistent
        // morphTargetsRelative made mergeGeometries() FAIL and silently fall back to geos[0] (hull only, no
        // sails/masts). Stripping the morph data lets all three meshes merge into the full mini ship.
        gg.morphAttributes = {}; gg.morphTargetsRelative = false;
        if (!gg.attributes.normal) gg.computeVertexNormals();
        if (!gg.attributes.uv) { const c = gg.attributes.position!.count; gg.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(c * 2), 2)); }
        gg.applyMatrix4(m.matrixWorld); geos.push(gg); if (!mat) mat = Array.isArray(m.material) ? m.material[0]! : m.material;
      });
      if (!geos.length || !mat) return;
      (mat as THREE.Material).side = THREE.DoubleSide;
      const merged = geos.length === 1 ? geos[0]! : (mergeGeometries(geos, false) ?? geos[0]!);
      const bbox = new THREE.Box3().setFromBufferAttribute(merged.attributes.position as THREE.BufferAttribute);
      const size = bbox.getSize(new THREE.Vector3());
      const scale = 0.62 / Math.max(size.x, size.z, 0.001); // ~0.62 m long mini on the map table
      this.shipBakes.set(cls, { geo: merged, mat, isGlb: true, scale, groundY: -bbox.min.y * scale });
      this.rebuildSlots();
    } catch (err) { console.warn('[house] ship model load failed', url, err); }
    finally { this.shipLoading.delete(cls); }
  }

  /** A fake museum spotlight for a filled trophy slot: a soft vertical beam of light + a gentle glow washing
   *  the piece. Both are camera-facing additive sprites (no hard cone silhouette, no real PointLight — six of
   *  those would blow the mobile budget). Reads as a display light picking the trophy out of the gloom. */
  private trophySpotlight(): THREE.Group {
    const g = new THREE.Group();
    const dot = this.softDotTex();
    const beam = new THREE.Sprite(new THREE.SpriteMaterial({ map: dot, color: 0xffeec4, transparent: true, opacity: 0.12, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending, fog: false }));
    beam.scale.set(0.6, 2.0, 1); beam.position.set(0.12, 1.8, 0.16); g.add(beam);
    const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: dot, color: 0xffe0a8, transparent: true, opacity: 0.16, depthWrite: false, blending: THREE.AdditiveBlending, fog: false }));
    glow.scale.set(0.44, 0.44, 1); glow.position.set(0.12, 1.42, 0.12); g.add(glow);
    return g;
  }

  /** An engraved brass plaque naming the feat — legible from close, reads as museum labelling not floating text. */
  private makeLabel(text: string): THREE.Sprite {
    const cv = document.createElement('canvas'); cv.width = 320; cv.height = 80;
    const ctx = cv.getContext('2d')!;
    const grad = ctx.createLinearGradient(0, 12, 0, 68);
    grad.addColorStop(0, '#8a6626'); grad.addColorStop(0.5, '#d0a951'); grad.addColorStop(1, '#6a4c18');
    ctx.fillStyle = grad;
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(10, 14, 300, 52, 9); ctx.fill(); } else { ctx.fillRect(10, 14, 300, 52); }
    ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(38,24,6,.85)'; ctx.stroke();
    ctx.font = 'bold 27px Georgia, serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(250,238,196,.5)'; ctx.fillText(text, 160, 39);   // top bevel highlight
    ctx.fillStyle = '#2a1c08'; ctx.fillText(text, 160, 41);                 // engraved dark
    const tex = new THREE.CanvasTexture(cv); tex.anisotropy = 4;
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
    spr.scale.set(2.2, 0.55, 1);
    return spr;
  }

  private clearGroup(g: THREE.Group): void {
    for (const c of [...g.children]) {
      g.remove(c);
      c.traverse((o) => {
        const s = o as THREE.Sprite;
        if (s.isSprite) { const sm = s.material as THREE.SpriteMaterial; sm.map?.dispose?.(); sm.dispose?.(); return; }
        const m = o as THREE.Mesh;
        if (m.isMesh && !this.isCachedGeo(m.geometry)) { // keep cached furniture geos/mats; dispose one-off slot geos
          m.geometry?.dispose?.(); const mm = m.material; if (Array.isArray(mm)) mm.forEach((x) => x.dispose?.()); else (mm as THREE.Material)?.dispose?.();
        }
      });
    }
  }
  private isCachedGeo(geo: THREE.BufferGeometry): boolean {
    // every baked hero GLB (berth/stove/candelabra + the Leva 2/3 heroes + the trophy ship minis) is CACHED and
    // instanced across rebuilds — never dispose its geo/material in clearGroup, or its textured material would be
    // freed and re-uploaded (or lost) on the next state.
    for (const b of [this.bedBake, this.stoveBake, this.candelabrumBake, this.cardTableBake, this.globeBake,
      this.aquariumBake, this.chartTableBake, this.bookshelfBake, this.strangeLampBake, this.rumBarrelBake, this.spyglassBake])
      if (b && b.geo === geo) return true;
    for (const b of this.furnBakes.values()) if (b.geo === geo || b.glowGeo === geo) return true;
    for (const b of this.shipBakes.values()) if (b.geo === geo) return true;
    return false;
  }

  /* ------------------------------- decorate ghost + edit camera ------------------------------- */

  /** Raycast a screen point onto the floor and return the grid cell under it, or null if outside the room. */
  pickCell(clientX: number, clientY: number): number | null {
    const ndc = new THREE.Vector2((clientX / window.innerWidth) * 2 - 1, -(clientY / window.innerHeight) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.floorPlane, hit)) return null;
    const gx = Math.floor(hit.x), gy = Math.floor(hit.z);
    if (gx < 0 || gy < 0 || gx >= HOUSE.WIDTH || gy >= HOUSE.DEPTH) return null;
    return gy * HOUSE.WIDTH + gx;
  }

  /** Show a translucent preview of `kind` at `cell`/`rot`, tinted by validity, at the given `scalePct` (v42) and
   *  optional v43 fine transform (`yaw8` 256-step rotation, `fx`/`fy` sub-cell offset; absent → legacy cardinal +
   *  centred). Mirrors exactly what rebuildFurniture will render so the ghost never lies. */
  showGhost(kind: number, cell: number, rot: number, valid: boolean, scalePct: number = HOUSE.SCALE_DEFAULT, yaw8?: number, fx?: number, fy?: number): void {
    if (kind !== this.ghostKind || !this.ghost) {
      if (this.ghost) { this.scene.remove(this.ghost); (this.ghost.material as THREE.Material).dispose?.(); }
      const bake = this.furnTemplate(kind);
      const geo = bake?.geo ?? new THREE.BoxGeometry(1, 1, 1);
      this.ghost = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.5, depthWrite: false }));
      this.ghost.frustumCulled = false;
      this.scene.add(this.ghost);
      this.ghostKind = kind;
    }
    const c = this.cellCentre(kind, cell, rot);
    const off = furnitureOffset(fx, fy);
    const pf = clampHouseScale(scalePct) / 100;
    const scale = (this.furnTemplate(kind)?.scale ?? 1) * pf, groundY = (this.furnTemplate(kind)?.groundY ?? 0) * pf;
    this.ghost.position.set(c.x + off.ox, groundY + 0.02, c.y + off.oy);
    this.ghost.rotation.y = furnitureYaw(rot, yaw8);
    this.ghost.scale.setScalar(scale);
    (this.ghost.material as THREE.MeshBasicMaterial).color.setHex(valid ? 0x55ff88 : 0xff5555);
  }

  hideGhost(): void {
    if (this.ghost) { this.scene.remove(this.ghost); (this.ghost.material as THREE.Material).dispose?.(); this.ghost = null; this.ghostKind = -1; }
  }

  /** Onda Editor-Paridade — screen-point hit-test against the REAL furniture meshes for the in-world editor's
   *  click-to-select (the layouteditor instanceId→prop pattern). Each kind renders as ONE InstancedMesh tagged
   *  `furnKind`; a hit resolves to that kind + the instance's index WITHIN its placement group (the same order
   *  rebuildFurniture groups `state.placed` by kind — so the editor maps it back to `pieces.filter(kind)[index]`).
   *  Returns null on empty floor (→ the caller lets OrbitControls orbit / deselects). */
  pickPlacement(clientX: number, clientY: number): { kind: number; index: number } | null {
    const ndc = new THREE.Vector2((clientX / window.innerWidth) * 2 - 1, -(clientY / window.innerHeight) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    const hits = this.raycaster.intersectObjects(this.furnitureRoot.children, false); // InstancedMeshes are direct children
    for (const h of hits) {
      const kind = h.object.userData.furnKind as number | undefined; // glow/flame instances + lights carry none → skipped
      if (kind !== undefined && h.instanceId != null) return { kind, index: h.instanceId };
    }
    return null;
  }

  /** Raise into an isometric decorate view (or drop back to the third-person follow). */
  setEditMode(on: boolean): void { this.editCam = on; if (!on) this.hideGhost(); }

  /** Onda Editor-em-Mundo — hand the camera to the in-world editor (its OrbitControls drive `this.camera`
   *  directly); updateFollowCamera becomes a no-op so it never fights the free orbit. */
  setEditorFreeCam(on: boolean): void { this._editorFreeCam = on; if (on) this.editCam = false; }

  /** Onda Sit-Configurador — force the ghost avatar to sit on an arbitrary configured seat for the LIVE preview
   *  (or stand, with null). The editor supplies `local` AT the seat each frame; sync re-asserts the pose. */
  override editorPreviewSit(spot: SeatSpot | null): void { this._editorSit = spot; if (!spot) this.standUp(); }

  protected override updateFollowCamera(local: { x: number; y: number; heading: number }, dt: number): void {
    if (this._editorFreeCam) {
      // the in-world editor owns the camera (OrbitControls) — it's already placed for this frame.
    } else if (!this.editCam) {
      super.updateFollowCamera(local, dt);
    } else {
      const W = HOUSE.WIDTH, D = HOUSE.DEPTH;
      const wantX = W / 2, wantY = 15, wantZ = D / 2 - 7;
      const k = 1 - Math.pow(0.002, dt);
      this.camPos.x += (wantX - this.camPos.x) * k; this.camPos.y += (wantY - this.camPos.y) * k; this.camPos.z += (wantZ - this.camPos.z) * k;
      this.camTarget.x += (W / 2 - this.camTarget.x) * k; this.camTarget.y += (0 - this.camTarget.y) * k; this.camTarget.z += (D / 2 + 1 - this.camTarget.z) * k;
      this.camera.position.copy(this.camPos); this.camera.lookAt(this.camTarget);
    }
    // camera is now placed for this frame → render the ExteriorVista portal (no-op on Low) BEFORE the base
    // render() samples it, then keep the roof from occluding the interior.
    this.renderVistaRT();
    this.applyRoofCull();
  }

  /** Keep the roof from ever blocking the interior. The ceiling + beams (roof=1) hide whenever the camera is
   *  above the walls (true for both the walking follow cam and the decorate cam — so the cabin is always
   *  legible from inside). roof=2 (the old mezzanine deck) is unused now that the two-story deck is gone —
   *  the cull hook stays for a future storey. Lights are untouched (they live outside the roof), so the interior
   *  keeps its warm cabin glow — not blown out. Nodes are collected lazily on first call (shell built in super()). */
  private collectRoofNodes(): void {
    if (this._roofNodes) return;
    this._roofNodes = []; this._mezzNodes = [];
    this.staticRoot.traverse((o) => {
      if (o.userData.roof === 1) this._roofNodes!.push(o);
      else if (o.userData.roof === 2) this._mezzNodes!.push(o);
    });
  }

  private applyRoofCull(): void {
    this.collectRoofNodes();
    // In the in-world House editor the free orbit can dip BELOW the wall line, so the game's "camera above the
    // walls" rule isn't enough on its own — the ceiling/beams would slam back into view (owner: "não sei por que
    // diabos tem TETO no editor"). Force the roof always-off under the editor free cam, exactly like the lid-less
    // City editor. Mezzanine (roof=2) hides in either editor mode too.
    const roofHidden = this._editorFreeCam || this.camera.position.y > WALL_H;
    for (const n of this._roofNodes!) n.visible = !roofHidden;
    for (const n of this._mezzNodes!) n.visible = !this.editCam && !this._editorFreeCam;
  }

  /** Editor-only: force the ceiling/beams + mezzanine hidden so a free top-down orbit sees the floor + furniture,
   *  never the lid. Idempotent + cheap; the editor calls it each frame because sync() only runs applyRoofCull when
   *  a local avatar is present (it isn't in the editor unless the Seat preview is actively sitting). */
  cullRoofForEditor(): void {
    this.collectRoofNodes();
    for (const n of this._roofNodes!) n.visible = false;
    for (const n of this._mezzNodes!) n.visible = false;
  }

  /* ------------------------------- helpers for main / prediction ------------------------------- */

  /** Shell posts + every placed SOLID furniture piece, as walker obstacles for the local prediction. */
  solidObstacles(): PortObstacle[] {
    const out: PortObstacle[] = HOUSE_SHELL.obstacles.map((o) => ({ x: o.x, y: o.y, r: o.r }));
    for (const p of this.state?.placed ?? []) {
      if (!furnitureById(p.kind)?.solid) continue;
      const c = this.cellCentre(p.kind, p.cell, p.rot);
      const fp = furnitureFootprint(p.kind, p.rot);
      out.push({ x: c.x, y: c.y, r: Math.max(0.3, Math.min(fp.w, fp.d) / 2) });
    }
    return out;
  }

  /** 0..1 fireside proximity to the hearth (drives the interior ambiance crossfade). */
  nearHearth(x: number, y: number): number {
    const d = Math.hypot(x - HOUSE_SHELL.hearth.x, y - HOUSE_SHELL.hearth.y);
    return Math.max(0, Math.min(1, 1 - d / HOUSE.HEARTH_AUDIO_R));
  }

  /** Walking into the door zone sails back to sea. */
  override atExit(x: number, y: number): boolean { return inHouseExit(x, y); }

  /** dig-ux (owner): the decorative Chart Table is a red herring — captains walk up to it expecting to make a
   *  treasure map. Standing beside a placed one surfaces an HONEST hint pointing at the real craft station (any
   *  port's Ship's Stores). Pure proximity read, mirrors nearHearth; no wire, no interaction surface. */
  nearChartTable(x: number, y: number): boolean {
    for (const p of this.state?.placed ?? []) {
      if (p.kind !== FurnitureKind.CHART_TABLE) continue;
      const c = this.cellCentre(p.kind, p.cell, p.rot);
      if (Math.hypot(x - c.x, y - c.y) <= 2.2) return true;
    }
    return false;
  }

  /** The inherited preload() also loads the market-stall GLB and calls placeStalls() — no stalls in a cabin,
   *  so this override neutralises it while still reusing the captain-avatar loading. */
  protected override placeStalls(): void { /* the house has no market stalls */ }
}
